import { useState } from 'react';
import Offer_Creation_Action from '../../../partials/offerCreation/Offer_Creation_Action';
import styled from 'styled-components';
import { Actions_, Confirmation_, device, Grey_, Main_ } from 'monica-alexandria';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import offer from '../offer.json';
import fields from '../fields.json';
import { service_catalog_items } from '../../../../utils/offers/service_catalog';
import { build_offer_report } from '../utils/build_offer_report';
import { calculate_combo_price } from '../utils/calculate_combo_price';
import { calculate_offer_totals } from '../utils/calculate_offer_totals';
import {
    creative_fixed_one_off_value,
    marketing_fixed_one_off_value,
    service_price_value
} from '../utils/marketing_service_rules';
import { industry_offer_content } from '../industry_offer_content';
import { industry_offer_content_en } from '../industry_offer_content_en';
import {
    rich_text_comment_html_for_preview,
    rich_text_has_content
} from '../utils/rich_text_comments';
import {
    offer_client_name_from_offer,
    offer_emails_from_offer,
    offer_reference_from_offer,
    selected_types_from_offer
} from '../../../../helpers/offerObject';
import { checkPermissions } from '../../../../helpers/checkPermissions';
import New_Offer_Price_Summary from './New_Offer_Price_Summary';
import { offer_catalog_type_key } from '../utils/offer_creation_rules';

const NewOfferPreviewPage = styled.div`
    display: grid;
    gap: 2rem;
    padding: 1.2rem;
    background: ${p => p.theme.background};

    @media print {
        padding: 0;
        background: ${p => p.theme.background};
    }
`;

const NewOfferPreviewActions = styled.div`
    width: min(100%, 78rem);
    margin: 0 auto;
    display: grid;
    gap: 1rem;

    @media print {
        display: none;
    }
`;

const NewOfferPreviewReviewerNote = styled.section`
    padding: 1.6rem;
    border: 0.3rem solid ${p => p.$hasDifferences ? '#c62828' : '#ef9f00'};
    border-radius: var(--smallRadius);
    background: ${p => p.$hasDifferences ? '#fff0f0' : '#fff8df'};
    color: #1d1d1d;
    box-shadow: 0 0.8rem 2rem rgba(0, 0, 0, 0.16);
    display: grid;
    gap: 1rem;

    h3,
    p {
        margin: 0;
    }

    ul {
        margin: 0;
        padding: 0;
        display: grid;
        gap: 0.7rem;
        list-style: none;
    }
`;

const NewOfferPreviewReviewerPricingRow = styled.li`
    padding: 0.9rem 1rem;
    border: 0.1rem solid ${p => p.$different ? '#c62828' : '#997000'};
    border-radius: var(--smallRadius);
    background: #ffffff;
    display: grid;
    grid-template-columns: minmax(12rem, 1fr) auto auto auto;
    gap: 0.8rem;
    align-items: center;

    strong {
        color: ${p => p.$different ? '#a31515' : '#4f3b00'};
    }

    @media ${device.sm} {
        grid-template-columns: 1fr;
    }
`;

const NewOfferPreviewPaper = styled.div`
    background: #ffffff;
    border-radius: 0;
    box-shadow: ${p => p.theme.out};
    overflow: hidden;
    border: none;
    width: min(100%, 78rem);
    margin: 0 auto;
    position: relative;
    font-family: 'Avenir Next', 'Segoe UI', Arial, sans-serif;

    h1, h2, h3, h4, h5, h6, p {
        color: #111111;
    }

    @media print {
        width: 100%;
        border: none;
        border-radius: 0;
        box-shadow: none;
    }
`;

const NewOfferPreviewCover = styled.div`
    background: #ffffff;
    padding: 2.4rem 2rem;
    display: grid;
    gap: 1.4rem;
    border-bottom: none;
    justify-items: center;

    @media (min-width: 72rem) {
        padding: 2.6rem 3rem 2.2rem;
        justify-items: center;
    }
`;

const NewOfferPreviewCoverLeft = styled.div`
    display: grid;
    gap: 1rem;
    justify-items: center;
    text-align: center;
    width: 100%;
`;

const NewOfferPreviewLogo = styled.div`
    width: 18rem;
    min-height: 5.4rem;
    border-radius: 0;
    background: #ffffff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.8rem 1.2rem;
    margin: 0 auto;
    box-shadow: none;
`;

const NewOfferPreviewLogoImage = styled.img`
    width: 100%;
    max-width: 15rem;
    height: auto;
    object-fit: contain;
`;

const NewOfferPreviewBrand = styled.h1`
    color: #111111;
    margin: 0;
    letter-spacing: 0.02rem;
    text-align: center;
`;

const NewOfferPreviewTitle = styled.div`
    display: grid;
    gap: 0.5rem;
    max-width: 45rem;
    text-align: center;

    h2 {
        color: #111111;
        letter-spacing: 0.01rem;
    }
`;

const NewOfferPreviewOfferCode = styled.h6`
    margin: 0;
    text-align: center;
    color: #111111;
    opacity: 0.75;
`;

const NewOfferPreviewSummary = styled.div`
    border-radius: 0;
    background: #ffffff;
    padding: 1.2rem 2rem;
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    text-align: left;
    border-bottom: none;

    @media (min-width: 72rem) {
        padding: 1.2rem 3rem;
    }
`;

const NewOfferPreviewSummaryItem = styled.div`
    display: grid;
    gap: 0.4rem;
    padding: 0.4rem 0;
    border-radius: 0;
    background: #ffffff;
    border: none;

    h4 {
        color: #111111;
        letter-spacing: 0.01rem;
    }

    p {
        color: #111111;
    }
`;

const NewOfferPreviewSummaryMeta = styled.div`
    display: grid;
    gap: 0.2rem;
    margin-top: 0.4rem;

    h6 {
        color: #111111;
        opacity: 0.85;
    }
`;

const NewOfferPreviewBody = styled.div`
    padding: 2rem;
    display: grid;
    gap: 1.2rem;
    position: relative;
    z-index: 2;

    @media (min-width: 72rem) {
        padding: 2rem 3rem 2.6rem;
    }
`;

const NewOfferPreviewSection = styled.section`
    background: #ffffff;
    border-radius: 0;
    box-shadow: none;
    padding: 1.6rem 0;
    display: grid;
    gap: 1rem;
    border: none;
    break-inside: avoid;
    page-break-inside: avoid;

    & + & {
        border-top: 0.1rem dashed rgba(0, 0, 0, 0.35);
    }

    h3 {
        color: #111111;
        letter-spacing: 0.01rem;
        margin: 0;
    }
`;

const NewOfferPreviewList = styled.ul`
    margin: 0;
    padding-left: 0;
    display: grid;
    gap: 0.8rem;
    list-style: none;
    font-family: "fontMedium";

    li {
        display: grid;
        grid-template-columns: 1.2rem 1fr;
        gap: 0.8rem;
        align-items: start;
        font-family: "fontMedium";
    }

    li p {
        font-family: "fontMedium";
    }

    li:before {
        content: '';
        width: 0.6rem;
        height: 0.6rem;
        border-radius: 50%;
        margin-top: 0.8rem;
        background: #111111;
    }
`;

const NewOfferPreviewFlow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
`;

const NewOfferPreviewFlowItem = styled.div`
    background: #ffffff;
    border-radius: 0;
    padding: 1rem 0;
    display: grid;
    gap: 0.8rem;
    border: none;
    box-shadow: none;
`;

const NewOfferPreviewFlowHead = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
`;

const NewOfferPreviewFlowIndex = styled.div`
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: #111111;
    display: grid;
    place-items: center;

    h6 {
        color: #ffffff;
    }
`;

const NewOfferPreviewTerms = styled.div`
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr;
`;

const NewOfferPreviewTerm = styled.div`
    background: #ffffff;
    border-radius: 0;
    padding: 0.8rem 0 1rem;
    display: grid;
    gap: 0.6rem;
    border: none;
`;

const NewOfferPreviewTermsList = styled.ul`
    margin: 0;
    padding-left: 0;
    display: grid;
    gap: 0.8rem;
    list-style: none;
    font-family: "fontMedium";

    li {
        display: block;
        font-family: "fontMedium";
    }

    li p {
        font-family: "fontMedium";
    }
`;

const NewOfferPreviewImportant = styled.div`
    display: grid;
    gap: 1rem;
`;

const NewOfferPreviewImportantItem = styled.div`
    background: #ffffff;
    border-radius: 0;
    padding: 0.8rem 0 1rem;
    display: grid;
    gap: 0.6rem;
    border: none;
`;

const NewOfferPreviewPricing = styled.div`
    display: grid;
    gap: 1.2rem;
`;

const NewOfferPreviewPricingGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;

    @media (min-width: 72rem) {
        grid-template-columns: 1fr 1fr;
    }
`;

const NewOfferPreviewPricingCard = styled.div`
    background: #ffffff;
    border: 0.1rem solid rgba(0, 0, 0, 0.35);
    border-radius: 0;
    padding: 1.2rem;
    display: grid;
    gap: 0.8rem;
`;

const NewOfferPreviewPricingHead = styled.div`
    padding-bottom: 0.5rem;
    border-bottom: 0.1rem solid rgba(0, 0, 0, 0.25);

    h4 {
        margin: 0;
    }
`;

const NewOfferPreviewPricingRow = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: end;
    gap: 1rem;

    p, h5 {
        margin: 0;
    }
`;

const NewOfferPreviewPricingTotal = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: end;
    gap: 1rem;
    padding-top: 0.8rem;
    border-top: 0.1rem solid rgba(0, 0, 0, 0.35);

    p, h3 {
        margin: 0;
    }
`;

const NewOfferPreviewPricingNote = styled.p`
    margin: 0;
`;

const NewOfferPreviewCatalog = styled.div`
    display: grid;
    gap: 1rem;
`;

const NewOfferPreviewCatalogBlock = styled.div`
    display: grid;
    gap: 0.8rem;
`;

const NewOfferPreviewCatalogType = styled.div`
    border: 0.1rem solid rgba(0, 0, 0, 0.35);
    border-radius: 0;
    background: #ffffff;
    padding: 1rem;
    display: grid;
    gap: 0.8rem;
`;

const NewOfferPreviewCatalogTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        padding: 0.7rem 0.5rem;
        border-bottom: 0.1rem solid rgba(0, 0, 0, 0.18);
        text-align: left;
    }

    th {
        background: rgba(0, 0, 0, 0.03);
    }

    tr:last-child td {
        border-bottom: none;
    }

    th:nth-child(n + 2),
    td:nth-child(n + 2) {
        text-align: right;
    }

    @media ${device.sm} {
        display: block;
        overflow-x: auto;
        white-space: nowrap;
    }
`;

const NewOfferPreviewCatalogSummary = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const NewOfferPreviewInternalNote = styled.div`
    margin-top: 0.6rem;
    padding: 0.8rem;
    border: 0.1rem dashed rgba(0, 0, 0, 0.35);
    background: #fafafa;
`;

const NewOfferPreviewQuestionnaire = styled(NewOfferPreviewInternalNote)`
    display: grid;
    gap: 0.8rem;
`;

const NewOfferPreviewQuestionnaireRows = styled.div`
    display: grid;
    gap: 0.7rem;
`;

const NewOfferPreviewQuestionnaireRow = styled.div`
    display: grid;
    gap: 0.2rem;

    h6,
    p {
        margin: 0;
    }

    p {
        white-space: pre-wrap;
    }
`;

const NewOfferPreviewServiceComment = styled.div`
    margin-top: 0.6rem;
    padding: 0.8rem;
    border: 0.1rem dotted rgba(0, 0, 0, 0.35);
    background: #ffffff;
`;

const NewOfferPreviewRichText = styled.div`
    display: grid;
    gap: 0.6rem;

    p {
        margin: 0;
    }

    ul,
    ol {
        margin: 0;
        padding-left: 1.8rem;
        font-family: "fontMedium";
    }

    li {
        font-family: "fontMedium";
    }

    li p,
    li span {
        font-family: "fontMedium";
    }

    li + li {
        margin-top: 0.4rem;
    }

    a {
        color: #111111;
        text-decoration: underline;
        word-break: break-word;
    }
`;

const NewOfferPreviewFooter = styled.div`
    background: #ffffff;
    border-radius: 0;
    padding: 2rem 2.4rem;
    display: grid;
    gap: 0.5rem;
    text-align: center;
    border-top: 0.1rem dashed rgba(0, 0, 0, 0.35);
    box-shadow: none;

    h4 {
        color: #111111;
    }

    h4, h5, p {
        color: #111111;
    }

    a {
        color: #111111;
        text-decoration: underline;
        word-break: break-all;
    }

    @media ${device.sm} {
        padding: 1.6rem 1.4rem;
    }

    @media print {
        break-inside: avoid;
        page-break-inside: avoid;
    }
`;

const fixed_one_off_value_by_service_key = (serviceKey = '') => {
    const marketingFixedValue = marketing_fixed_one_off_value(serviceKey);

    if (marketingFixedValue !== null) {
        return marketingFixedValue;
    }

    const creativeFixedValue = creative_fixed_one_off_value(serviceKey);

    if (creativeFixedValue !== null) {
        return creativeFixedValue;
    }

    if (serviceKey === 'website_creation_eshop' || serviceKey === 'website_eshop' || serviceKey === 'website_plan_eshop') {
        return true;
    }

    if (serviceKey.startsWith('website_creation_custom_')) {
        return true;
    }

    if (serviceKey.startsWith('website_creation_plan_') || serviceKey.startsWith('website_plan_')) {
        return true;
    }

    if (serviceKey.startsWith('website_maintenance_')) {
        return false;
    }

    return null;
};

export const selected_services = (offerDraft) => {
    let rawSelectedTypes = offerDraft?.selectedTypes || [];

    if (!rawSelectedTypes.length) {
        rawSelectedTypes = offerDraft?.typesData?.selectedTypes || [];
    }

    if (!rawSelectedTypes.length) {
        rawSelectedTypes = selected_types_from_offer(offerDraft);
    }

    const selectedTypes = [...new Set(rawSelectedTypes.map(offer_catalog_type_key))];
    const selectedServices = offerDraft?.setupData?.selectedServices || {};
    const servicePriceOverwriteValues = offerDraft?.setupData?.servicePriceOverwriteValues || {};
    const serviceOneOffValues = offerDraft?.setupData?.serviceOneOffValues || {};
    const customServicesByType = offerDraft?.setupData?.customServicesByType || {};
    const defaultServices = [];
    const manualServices = [];

    service_catalog_items().forEach(({ serviceKey, sourceType, catalogType, serviceValue }) => {
        defaultServices.push({
            serviceKey,
            serviceType: catalogType,
            serviceSourceType: sourceType,
            serviceText: serviceValue.text,
            servicePriceDefault: typeof serviceValue.price === 'number' ? serviceValue.price : serviceValue.price?.suggested ?? 0,
            serviceOneOffDefault: serviceValue.one_off ?? false,
            serviceVatIncludedDefault: serviceValue.vat_included ?? false
        });
    });

    selectedTypes.forEach((typeName) => {
        if (typeName === 'website_creation') {
            (customServicesByType[typeName] || []).forEach((service) => {
                manualServices.push({
                    serviceKey: service.serviceKey,
                    serviceType: typeName,
                    serviceText: service.text,
                    servicePriceDefault: typeof service.price === 'number' ? service.price : 0,
                    serviceOneOffDefault: true,
                    serviceVatIncludedDefault: false
                });
            });
        }
    });

    return [...defaultServices, ...manualServices]
        .filter((service) => selectedServices[service.serviceKey] && service.serviceText)
        .map((service) => {
            const nextPrice = service_price_value(service.serviceKey, service.servicePriceDefault, servicePriceOverwriteValues);
            const fixedOneOffValue = fixed_one_off_value_by_service_key(service.serviceKey);
            const nextOneOff = fixedOneOffValue === null
                ? serviceOneOffValues[service.serviceKey] ?? service.serviceOneOffDefault
                : fixedOneOffValue;

            return {
                ...service,
                servicePrice: Number(nextPrice) || 0,
                serviceOneOff: nextOneOff,
                serviceVatIncluded: service.serviceVatIncludedDefault ?? false
            };
        });
};

const normalize_service_key = (serviceKey) => {
    const formattedServiceKey = `${serviceKey || ''}`.replace(/\s+/g, '_').toLowerCase();

    if (formattedServiceKey.startsWith('website_creation_')) {
        return formattedServiceKey.replace(/^website_creation_/, '');
    }

    return formattedServiceKey
        .split('_')
        .slice(1)
        .join('_')
        .replace(/\s+/g, '_')
        .toLowerCase();
};

const available_services_for_selected_types = (selectedTypes = []) => {
    const catalogTypes = selectedTypes.map(offer_catalog_type_key);
    return service_catalog_items()
        .filter((service) => catalogTypes.includes(service.catalogType) && service.serviceValue.status && !service.serviceValue.retired)
        .map((service) => ({
            serviceKey: normalize_service_key(service.serviceKey),
            serviceText: service.serviceValue.text
        }));
};

const eur = (value) => {
    return `€ ${Number(value || 0).toFixed(2)}`;
};

const round_money = (value) => {
    return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
};

const to_number = (value) => {
    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
        return parsedValue;
    }

    return 0;
};

const price_number_or_null = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
        return parsedValue;
    }

    return null;
};

const first_price_number = (...values) => {
    const matchingValue = values
        .map((value) => price_number_or_null(value))
        .find((value) => value !== null);

    return matchingValue === undefined ? null : matchingValue;
};

const price_data_has_saved_amounts = (priceData = {}) => {
    return first_price_number(
        priceData?.total,
        priceData?.totalWithVat,
        priceData?.totalNetAmount,
        priceData?.subtotal,
        priceData?.recurringTotalWithVat,
        priceData?.oneOffTotalWithVat,
        priceData?.recurringNetAmount,
        priceData?.oneOffNetAmount,
        priceData?.mixedRecurringManualNetAmount,
        priceData?.mixedOneOffManualNetAmount
    ) !== null;
};

const net_amount_from_total = (totalValue = 0, vatRate = 0) => {
    const totalNumber = Number(totalValue) || 0;
    const vatRateNumber = Number(vatRate) || 0;

    if (vatRateNumber <= 0) {
        return round_money(totalNumber);
    }

    return round_money(totalNumber / (1 + vatRateNumber));
};

const vat_amount_from_total = (totalValue = 0, vatRate = 0) => {
    const totalNumber = Number(totalValue) || 0;

    return round_money(totalNumber - net_amount_from_total(totalNumber, vatRate));
};

const adspend_defaults = {
    meta_ads: 150,
    google_ads: 300,
    tiktok_ads: 600
};

const read_service_adspend = (offerDraft, serviceKey, serviceItems) => {
    const setupData = offerDraft?.setupData || {};
    const selectedServices = serviceItems || selected_services(offerDraft);
    const matchingService = selectedServices.find((service) => normalize_service_key(service.serviceKey) === serviceKey);
    const defaultValue = adspend_defaults[serviceKey] ?? 0;

    if (!matchingService?.serviceKey) {
        return 0;
    }

    const savedValue = setupData?.serviceAdSpendValues?.[matchingService.serviceKey];

    if (savedValue === '' || savedValue === null || savedValue === undefined) {
        return defaultValue;
    }

    return Number(savedValue) || 0;
};

const read_service_section_budget = (offerDraft, serviceSection, serviceItems, offerLanguage = 'el') => {
    const sectionServiceKeys = Array.isArray(serviceSection?.serviceKeys) ? serviceSection.serviceKeys : [];
    const adSpendServiceKeys = sectionServiceKeys.filter((serviceKey) => {
        return serviceKey === 'meta_ads' || serviceKey === 'google_ads' || serviceKey === 'tiktok_ads';
    });

    if (!adSpendServiceKeys.length) {
        return serviceSection?.recommendedBudget || '';
    }

    const sectionAdSpend = adSpendServiceKeys.reduce((sum, serviceKey) => {
        return sum + read_service_adspend(offerDraft, serviceKey, serviceItems);
    }, 0);

    return offerLanguage === 'en'
        ? `${eur(round_money(sectionAdSpend))} / 30 days`
        : `${eur(round_money(sectionAdSpend))} / 30 ημέρες`;
};

const questionnaireOtherValue = 'other';

const questionnaire_other_key = (questionKey = '') => {
    return `${questionKey}_other`;
};

const websiteQuestionOptions = {
    companyIndustry: [
        { value: 'retail', label: 'Λιανική (Retail)' },
        { value: 'services', label: 'Υπηρεσίες (Services)' },
        { value: 'hospitality', label: 'Φιλοξενία (Hospitality)' },
        { value: 'technology', label: 'Τεχνολογία (Technology)' },
        { value: 'non_profit', label: 'Μη Κερδοσκοπικός (Non-profit)' },
        { value: questionnaireOtherValue, label: 'Άλλο' }
    ],
    targetAudience: [
        { value: 'b2b', label: 'B2B' },
        { value: 'b2c', label: 'B2C' },
        { value: 'both', label: 'Και τα δύο (Both)' }
    ],
    websiteGoals: [
        { value: 'showcase', label: 'Παρουσίαση προϊόντων ή υπηρεσιών (Showcase products or services)' },
        { value: 'leads', label: 'Συλλογή leads (Generate leads)' },
        { value: 'online_sales', label: 'Ηλεκτρονικές πωλήσεις (Online sales - e-commerce)' },
        { value: 'company_information', label: 'Εταιρική παρουσίαση (Share company information)' },
        { value: 'blog_news', label: 'Blog ή νέα (Blog or news updates)' },
        { value: 'customer_support', label: 'Υποστήριξη πελατών / FAQ (Customer support / FAQs)' },
        { value: 'community', label: 'Δημιουργία κοινότητας (Build community)' },
        { value: questionnaireOtherValue, label: 'Άλλο' }
    ],
    basicFeatures: [
        { value: 'contact_form', label: 'Φόρμα Επικοινωνίας (Contact form)' },
        { value: 'newsletter', label: 'Εγγραφή Newsletter (Newsletter signup)' },
        { value: 'blog_news', label: 'Blog ή νέα (Blog or news updates)' },
        { value: 'image_gallery', label: 'Γκαλερί εικόνων (Image gallery)' },
        { value: 'video_gallery', label: 'Γκαλερί βίντεο (Video gallery)' },
        { value: 'reviews', label: 'Κριτικές (Testimonials / reviews section)' },
        { value: 'search', label: 'Αναζήτηση (Search function)' },
        { value: 'faq', label: 'FAQ (Συχνές Ερωτήσεις & Απαντήσεις)' },
        { value: 'downloadable_files', label: 'Αρχεία για κατέβασμα (Downloadable files)' },
        { value: questionnaireOtherValue, label: 'Άλλο' }
    ],
    advancedFeatures: [
        { value: 'multilingual', label: 'Πολυγλωσσικό (Multilingual website)' },
        { value: 'membership', label: 'Μέλη / Σύνδεση χρήστη (Membership / user login)' },
        { value: 'forum', label: 'Forum ή κοινότητα (Forum / community area)' },
        { value: 'live_chat', label: 'Live chat ή chatbot (Chatbot or live chat)' },
        { value: 'event_calendar', label: 'Ημερολόγιο εκδηλώσεων (Event calendar)' },
        { value: 'interactive_map', label: 'Διαδραστικός χάρτης (Interactive map / store locator)' },
        { value: 'portfolio', label: 'Portfolio' },
        { value: 'third_party_apps', label: 'Σύνδεση με 3rd party apps' },
        { value: 'social_feeds', label: 'Σύνδεση με social media (Social media feeds)' },
        { value: 'custom_forms', label: 'Custom φόρμες (Custom forms / surveys)' },
        { value: 'donation_system', label: 'Σύστημα δωρεών (Donation system)' }
    ],
    technicalFeatures: [
        { value: 'seo', label: 'SEO (Search Engine Optimization)' },
        { value: 'google_analytics', label: 'Google Analytics setup' },
        { value: 'gdpr', label: 'Cookie consent / GDPR' },
        { value: 'accessibility', label: 'Προσβασιμότητα (Accessibility compliance - WCAG)' },
        { value: 'security', label: 'Ασφάλεια (Security: SSL, backups)' },
        { value: 'speed_optimization', label: 'Βελτιστοποίηση ταχύτητας (Speed optimization)' }
    ],
    domain: [
        { value: 'yes', label: 'Ναι' },
        { value: 'no', label: 'Όχι' }
    ],
    hosting: [
        { value: 'yes', label: 'Ναι' },
        { value: 'no', label: 'Όχι' },
        { value: questionnaireOtherValue, label: 'Άλλο' }
    ],
    starterPages: [
        { value: 'home', label: 'Αρχική' },
        { value: 'about', label: 'Σχετικά με εμάς' },
        { value: 'services', label: 'Υπηρεσίες' },
        { value: 'portfolio', label: 'Portfolio' },
        { value: 'contact', label: 'Επικοινωνία' },
        { value: 'blog', label: 'Blog / Νέα' },
        { value: questionnaireOtherValue, label: 'Άλλο' }
    ]
};

const websiteQuestionnaireQuestions = {
    starter: [
        { questionKey: 'company_industry', questionLabel: 'Σχετικά με την Εταιρεία σας - Κατηγορία (Category)', options: websiteQuestionOptions.companyIndustry, allowOther: true },
        { questionKey: 'target_audience', questionLabel: 'Στοχευόμενο κοινό', options: websiteQuestionOptions.targetAudience },
        { questionKey: 'starter_project_scope', questionLabel: 'Περιγράψτε με λίγα λόγια τον σκοπό του website' },
        { questionKey: 'website_goals', questionLabel: 'Στόχοι Ιστοσελίδας', options: websiteQuestionOptions.websiteGoals, allowOther: true },
        { questionKey: 'starter_pages', questionLabel: 'Ποιες βασικές σελίδες χρειάζεστε;', options: websiteQuestionOptions.starterPages, allowOther: true },
        { questionKey: 'has_domain', questionLabel: 'Έχετε domain; παράδειγμα (www.mywebsite.gr)', options: websiteQuestionOptions.domain },
        { questionKey: 'has_hosting', questionLabel: 'Έχετε hosting;', options: websiteQuestionOptions.hosting, allowOther: true }
    ],
    business: [
        { questionKey: 'company_industry', questionLabel: 'Σχετικά με την Εταιρεία σας - Κατηγορία (Category)', options: websiteQuestionOptions.companyIndustry, allowOther: true },
        { questionKey: 'business_products_services', questionLabel: 'Κύρια προϊόντα ή υπηρεσίες' },
        { questionKey: 'target_audience', questionLabel: 'Στοχευόμενο κοινό', options: websiteQuestionOptions.targetAudience },
        { questionKey: 'website_goals', questionLabel: 'Στόχοι Ιστοσελίδας', options: websiteQuestionOptions.websiteGoals, allowOther: true },
        { questionKey: 'basic_features', questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Βασικά (Basic Features)', options: websiteQuestionOptions.basicFeatures, allowOther: true },
        { questionKey: 'advanced_features', questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Προχωρημένα (Advanced Features)', options: websiteQuestionOptions.advancedFeatures },
        { questionKey: 'technical_features', questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Τεχνικά (Technical Features)', options: websiteQuestionOptions.technicalFeatures },
        { questionKey: 'business_integrations_notes', questionLabel: 'Υπάρχουν integrations ή τεχνικές απαιτήσεις που πρέπει να γνωρίζουμε;' },
        { questionKey: 'has_domain', questionLabel: 'Έχετε domain; παράδειγμα (www.mywebsite.gr)', options: websiteQuestionOptions.domain },
        { questionKey: 'has_hosting', questionLabel: 'Έχετε hosting;', options: websiteQuestionOptions.hosting, allowOther: true }
    ],
    eshop: [
        { questionKey: 'company_industry', questionLabel: 'Σχετικά με την Εταιρεία σας - Κατηγορία (Category)', options: websiteQuestionOptions.companyIndustry, allowOther: true },
        { questionKey: 'main_products_services', questionLabel: 'Κύρια προϊόντα ή υπηρεσίες' },
        { questionKey: 'target_audience', questionLabel: 'Στοχευόμενο κοινό', options: websiteQuestionOptions.targetAudience },
        { questionKey: 'website_goals', questionLabel: 'Στόχοι Ιστοσελίδας', options: websiteQuestionOptions.websiteGoals, allowOther: true },
        { questionKey: 'basic_features', questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Βασικά (Basic Features)', options: websiteQuestionOptions.basicFeatures, allowOther: true },
        { questionKey: 'advanced_features', questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Προχωρημένα (Advanced Features)', options: websiteQuestionOptions.advancedFeatures },
        { questionKey: 'technical_features', questionLabel: 'Απαιτούμενα Χαρακτηριστικά Ιστοσελίδας - Τεχνικά (Technical Features)', options: websiteQuestionOptions.technicalFeatures },
        { questionKey: 'has_domain', questionLabel: 'Έχετε domain; παράδειγμα (www.mywebsite.gr)', options: websiteQuestionOptions.domain },
        { questionKey: 'has_hosting', questionLabel: 'Έχετε hosting;', options: websiteQuestionOptions.hosting, allowOther: true }
    ]
};

const option_label_from_value = (questionItem = {}, value = '', answers = {}) => {
    const optionItem = (questionItem.options || []).find((item) => item.value === value);

    if (value === questionnaireOtherValue && questionItem.allowOther) {
        const otherValue = `${answers?.[questionnaire_other_key(questionItem.questionKey)] || ''}`.trim();

        if (otherValue.length) {
            return `${optionItem?.label || 'Άλλο'}: ${otherValue}`;
        }
    }

    return optionItem?.label || `${value || ''}`.trim();
};

const questionnaire_answer_text = (questionItem = {}, answers = {}, fallbackValue = '—') => {
    const answerValue = answers?.[questionItem.questionKey];

    if (Array.isArray(answerValue)) {
        const answerLabels = answerValue
            .map((value) => option_label_from_value(questionItem, value, answers))
            .filter((value) => `${value || ''}`.trim().length);

        return answerLabels.length ? answerLabels.join(', ') : fallbackValue;
    }

    const normalizedAnswer = `${answerValue || ''}`.trim();

    if (!normalizedAnswer.length) {
        return fallbackValue;
    }

    return option_label_from_value(questionItem, normalizedAnswer, answers);
};

const questionnaire_has_answers = (answers = {}) => {
    return Object.keys(answers || {}).some((answerKey) => {
        return !answerKey.endsWith('_other') && `${answers[answerKey] || ''}`.trim().length;
    });
};

const questionnaire_rows = (questionnaireType = '', answers = {}, fallbackValue = '—') => {
    if (!questionnaire_has_answers(answers)) {
        return [];
    }

    return (websiteQuestionnaireQuestions[questionnaireType] || []).map((questionItem) => {
        return {
            question: questionItem.questionLabel,
            answer: questionnaire_answer_text(questionItem, answers, fallbackValue)
        };
    });
};

const questionnaire_type_from_service_keys = (serviceKeys = []) => {
    if (serviceKeys.includes('plan_starter')) {
        return 'starter';
    }

    if (serviceKeys.includes('plan_business')) {
        return 'business';
    }

    if (serviceKeys.includes('eshop') || serviceKeys.includes('plan_eshop')) {
        return 'eshop';
    }

    return '';
};

const social_posts_per_month = (offerDraft) => {
    const selectedServices = offerDraft?.setupData?.selectedServices || {};
    const serviceDealValues = offerDraft?.setupData?.serviceDealValues || {};
    const selectedSocialServiceKey = Object.keys(selectedServices).find((serviceKey) => {
        return selectedServices[serviceKey] && serviceKey.endsWith('_social_media_management');
    });
    const serviceTypeName = selectedSocialServiceKey ? selectedSocialServiceKey.split('_')[0] : 'marketing';
    const defaultDealValues = fields[serviceTypeName]?.social_media_management?.deal || fields?.marketing?.social_media_management?.deal || {};
    const selectedDealValues = selectedSocialServiceKey ? serviceDealValues[selectedSocialServiceKey] || {} : {};
    const reelsPerMonth = to_number(selectedDealValues?.reel ?? defaultDealValues?.reel ?? 0);
    const staticPostsPerMonth = to_number(selectedDealValues?.static ?? defaultDealValues?.static ?? 0);

    return reelsPerMonth + staticPostsPerMonth;
};

const service_summary_prefix = (selectedTypes = [], offerLanguage = 'el') => {
    const isEnglish = offerLanguage === 'en';
    const hasMarketing = selectedTypes.includes('marketing');
    const hasGraphic = selectedTypes.includes('graphic') || selectedTypes.includes('creative');
    const hasWebsite = selectedTypes.includes('website')
        || selectedTypes.includes('website_creation')
        || selectedTypes.includes('website_maintenance');
    const hasSass = selectedTypes.includes('sass');

    if (hasSass && selectedTypes.length === 1) {
        return isEnglish ? 'SASS services' : 'Υπηρεσίες SASS';
    }

    if (hasMarketing && hasGraphic && hasWebsite) {
        return isEnglish
            ? 'Personalised ads, website and One-offs services'
            : 'Υπηρεσίες Personalised ads, website & One-offs';
    }

    if (hasMarketing && hasWebsite) {
        return isEnglish ? 'Personalised ads and website services' : 'Υπηρεσίες Personalised ads & website';
    }

    if (hasGraphic && hasWebsite) {
        return isEnglish ? 'Website and One-offs services' : 'Υπηρεσίες website & One-offs';
    }

    if (hasMarketing && hasGraphic) {
        return isEnglish ? 'Personalised ads and One-offs services' : 'Υπηρεσίες Personalised ads & One-offs';
    }

    if (hasMarketing) {
        return isEnglish ? 'Personalised ads services' : 'Υπηρεσίες Personalised ads';
    }

    if (hasGraphic) {
        return isEnglish ? 'One-offs services' : 'Υπηρεσίες One-offs';
    }

    if (hasWebsite) {
        return isEnglish ? 'Website services' : 'Υπηρεσίες website';
    }

    return isEnglish ? 'Services' : 'Υπηρεσίες';
};

const report_content_key = (selectedTypes = [], industryKey = '') => {
    const hasWebsite = selectedTypes.includes('website')
        || selectedTypes.includes('website_creation')
        || selectedTypes.includes('website_maintenance');

    const hasGraphic = selectedTypes.includes('graphic') || selectedTypes.includes('creative');

    if (hasWebsite && !selectedTypes.includes('marketing') && !hasGraphic) {
        return 'website';
    }

    if (hasGraphic && !selectedTypes.includes('marketing') && !hasWebsite) {
        return 'graphic';
    }

    return industryKey;
};

const merged_content_by_template = (industryKey = '', contentKey = '', contentMap = industry_offer_content) => {
    const defaultContent = contentMap.general || {};
    const industryContent = contentMap[industryKey] || {};
    const requestedContent = contentMap[contentKey] || {};

    return {
        ...defaultContent,
        ...industryContent,
        ...requestedContent
    };
};

const text_nodes_from_content = (value) => {
    if (typeof value === 'string') {
        const normalizedValue = value.trim();
        return normalizedValue.length ? [normalizedValue] : [];
    }

    if (Array.isArray(value)) {
        return value.flatMap((arrayValue) => text_nodes_from_content(arrayValue));
    }

    if (!value || typeof value !== 'object') {
        return [];
    }

    return Object.keys(value)
        .sort()
        .flatMap((key) => text_nodes_from_content(value[key]));
};

const offer_content_template_fingerprint = (industryKey = '', contentKey = '', contentMap = industry_offer_content) => {
    const mergedTemplateContent = merged_content_by_template(industryKey, contentKey, contentMap);
    return text_nodes_from_content(mergedTemplateContent).join('\n');
};

const type_title_by_key = (typeKey = '') => {
    if (typeKey === 'marketing') {
        return 'Personalised ads';
    }

    if (typeKey === 'graphic' || typeKey === 'creative') {
        return 'One-offs';
    }

    if (typeKey === 'website') {
        return 'Website';
    }

    if (typeKey === 'website_creation') {
        return 'Website Creation';
    }

    if (typeKey === 'website_maintenance') {
        return 'Website Maintenance';
    }

    if (typeKey === 'sass') {
        return 'SASS';
    }

    return typeKey;
};

const offer_type_title_by_language = (typeKey = '', offerLanguage = 'el') => {
    if (offerLanguage === 'en') {
        return type_title_by_key(typeKey);
    }

    if (typeKey === 'marketing') {
        return 'Personalised ads';
    }

    if (typeKey === 'graphic' || typeKey === 'creative') {
        return 'One-offs';
    }

    if (typeKey === 'website') {
        return 'Website';
    }

    if (typeKey === 'website_creation') {
        return 'Website Creation';
    }

    if (typeKey === 'website_maintenance') {
        return 'Website Maintenance';
    }

    if (typeKey === 'sass') {
        return 'SASS';
    }

    return typeKey;
};

const clamp_discount_percentage = (value) => {
    const parsedValue = Number(value) || 0;

    if (parsedValue > 100) {
        return 100;
    }

    return parsedValue;
};

const service_name_by_service_item = (serviceItem = {}) => {
    const serviceType = `${serviceItem.serviceSourceType || serviceItem.serviceType || ''}`.trim();

    return String(serviceItem.serviceKey || '').replace(`${serviceType}_`, '');
};

const customer_catalog_row = (catalogRow = {}) => {
    const discountValue = to_number(catalogRow.discountValue);
    const discountPercent = to_number(catalogRow.discountPercent);

    if (discountValue >= 0 && discountPercent >= 0) {
        return catalogRow;
    }

    const finalPrice = to_number(catalogRow.finalPrice);

    return {
        ...catalogRow,
        catalogPrice: finalPrice,
        discountPercent: 0,
        discountValue: 0,
        finalPrice
    };
};

const catalog_discount_summary = (catalogRows = []) => {
    const catalogSubtotal = catalogRows.reduce((totalValue, catalogRow) => {
        return totalValue + to_number(catalogRow.catalogPrice);
    }, 0);
    const discountValue = catalogRows.reduce((totalValue, catalogRow) => {
        return totalValue + to_number(catalogRow.discountValue);
    }, 0);
    const discountPercent = catalogSubtotal > 0
        ? clamp_discount_percentage((discountValue / catalogSubtotal) * 100)
        : 0;

    return {
        discountValue,
        discountPercent
    };
};

const to_priced_service_item = (serviceItem = {}) => {
    return {
        ...serviceItem,
        servicePriceNumber: to_number(serviceItem.servicePrice),
        serviceVatIncluded: Boolean(serviceItem.serviceVatIncluded),
        serviceType: serviceItem.serviceType || '',
        serviceName: service_name_by_service_item(serviceItem)
    };
};

const type_pricing_catalog = (serviceItems = [], priceData = {}) => {
    const oneOffServicesAreFree = serviceItems.some((serviceItem) => !serviceItem.serviceOneOff)
        && serviceItems.some((serviceItem) => serviceItem.serviceOneOff);
    const servicesByType = serviceItems.reduce((typeLookup, serviceItem) => {
        const typeKey = serviceItem.serviceType || 'service';
        const currentTypeItems = typeLookup[typeKey] || [];

        return {
            ...typeLookup,
            [typeKey]: [...currentTypeItems, serviceItem]
        };
    }, {});

    return Object.entries(servicesByType).map(([typeKey, typeServices]) => {
        const billableTypeServices = oneOffServicesAreFree
            ? typeServices.filter((serviceItem) => !serviceItem.serviceOneOff)
            : typeServices;
        const billableCatalogSubtotal = billableTypeServices.reduce((totalPrice, serviceItem) => {
            return totalPrice + to_number(serviceItem.servicePrice);
        }, 0);
        const typeServiceNames = billableTypeServices.map(service_name_by_service_item);
        const typeServicePrices = billableTypeServices.reduce((servicePriceLookup, serviceItem) => {
            return {
                ...servicePriceLookup,
                [service_name_by_service_item(serviceItem)]: to_number(serviceItem.servicePrice)
            };
        }, {});
        const configuredSubtotal = billableTypeServices.reduce((totalPrice, serviceItem) => {
            return totalPrice + to_number(serviceItem.servicePrice);
        }, 0);
        const comboTotals = typeKey === 'marketing' && billableTypeServices.length
            ? calculate_combo_price(typeServiceNames, typeServicePrices)
            : null;
        const subtotalBeforeDiscount = comboTotals ? comboTotals.total : configuredSubtotal;
        const typeDiscountPercent = clamp_discount_percentage(priceData?.typeDiscountValues?.[typeKey] || 0);
        const typeDiscountAmount = subtotalBeforeDiscount * (typeDiscountPercent / 100);
        const typeTotalAfterDiscount = subtotalBeforeDiscount - typeDiscountAmount;
        const billableDiscountValue = billableCatalogSubtotal - typeTotalAfterDiscount;
        const billableDiscountPercent = billableCatalogSubtotal > 0
            ? clamp_discount_percentage((billableDiscountValue / billableCatalogSubtotal) * 100)
            : 0;
        const catalogRows = typeServices.map((serviceItem) => {
            const serviceCatalogPrice = to_number(serviceItem.servicePrice);
            const serviceIsFreeOneOff = oneOffServicesAreFree && serviceItem.serviceOneOff;

            if (serviceIsFreeOneOff) {
                return {
                    serviceKey: serviceItem.serviceKey,
                    serviceName: serviceItem.serviceText,
                    serviceOneOff: true,
                    catalogPrice: serviceCatalogPrice,
                    discountPercent: 100,
                    discountValue: serviceCatalogPrice,
                    finalPrice: 0
                };
            }

            const serviceDiscountValue = serviceCatalogPrice * (billableDiscountPercent / 100);
            const serviceFinalPrice = serviceCatalogPrice - serviceDiscountValue;

            return {
                serviceKey: serviceItem.serviceKey,
                serviceName: serviceItem.serviceText,
                serviceOneOff: Boolean(serviceItem.serviceOneOff),
                catalogPrice: serviceCatalogPrice,
                discountPercent: billableDiscountPercent,
                discountValue: serviceDiscountValue,
                finalPrice: serviceFinalPrice
            };
        });
        const customerCatalogRows = catalogRows.map(customer_catalog_row);
        const customerDiscountSummary = catalog_discount_summary(customerCatalogRows);

        return {
            typeKey,
            typeTitle: type_title_by_key(typeKey),
            totalDiscountValue: customerDiscountSummary.discountValue,
            totalDiscountPercent: customerDiscountSummary.discountPercent,
            catalogRows: customerCatalogRows
        };
    });
};

const type_pricing_catalog_by_payment = (catalogByType = [], paymentType = 'recurring', discountPercentOverride = null) => {
    const isOneOffMode = paymentType === 'one_off';
    const hasOverrideDiscountPercent = Number.isFinite(Number(discountPercentOverride));
    const normalizedOverrideDiscountPercent = hasOverrideDiscountPercent
        ? clamp_discount_percentage(discountPercentOverride)
        : 0;

    return catalogByType
        .map((typeCatalogItem) => {
            const catalogRows = typeCatalogItem.catalogRows.filter((catalogRow) => {
                return isOneOffMode ? catalogRow.serviceOneOff : !catalogRow.serviceOneOff;
            }).map((catalogRow) => {
                if (!hasOverrideDiscountPercent) {
                    return catalogRow;
                }

                const discountValue = to_number(catalogRow.catalogPrice) * (normalizedOverrideDiscountPercent / 100);
                const finalPrice = to_number(catalogRow.catalogPrice) - discountValue;

                return {
                    ...catalogRow,
                    discountPercent: normalizedOverrideDiscountPercent,
                    discountValue,
                    finalPrice
                };
            });
            const customerCatalogRows = catalogRows.map(customer_catalog_row);
            const customerDiscountSummary = catalog_discount_summary(customerCatalogRows);

            return {
                ...typeCatalogItem,
                totalDiscountValue: customerDiscountSummary.discountValue,
                totalDiscountPercent: customerDiscountSummary.discountPercent,
                catalogRows: customerCatalogRows
            };
        })
        .filter((typeCatalogItem) => typeCatalogItem.catalogRows.length);
};

const catalog_subtotal_by_payment = (catalogByType = [], paymentType = 'recurring') => {
    const isOneOffMode = paymentType === 'one_off';

    return (catalogByType || []).reduce((totalSubtotal, typeCatalogItem) => {
        const typeSubtotal = (typeCatalogItem?.catalogRows || []).reduce((catalogSubtotal, catalogRow) => {
            const rowMatchesPayment = isOneOffMode ? catalogRow?.serviceOneOff : !catalogRow?.serviceOneOff;

            if (!rowMatchesPayment) {
                return catalogSubtotal;
            }

            return catalogSubtotal + to_number(catalogRow?.catalogPrice);
        }, 0);

        return totalSubtotal + typeSubtotal;
    }, 0);
};

const payment_discount_percent_by_manual = (catalogSubtotal = 0, manualNetAmount = null) => {
    if (!Number.isFinite(catalogSubtotal) || catalogSubtotal <= 0) {
        return null;
    }

    if (!Number.isFinite(manualNetAmount) || manualNetAmount < 0) {
        return null;
    }

    const discountPercent = ((catalogSubtotal - manualNetAmount) / catalogSubtotal) * 100;

    return clamp_discount_percentage(discountPercent);
};

const companyLogoUrl = 'https://genius1071.friktoriaservers.net/doitforme/logo/logo_light.svg';

const read_valid_offer_language = (languageValue = '') => {
    const normalizedLanguage = `${languageValue || ''}`.trim().toLowerCase();

    if (normalizedLanguage === 'en' || normalizedLanguage === 'el') {
        return normalizedLanguage;
    }

    return '';
};

const resolve_offer_language = (offerDraft = {}, typesData = {}) => {
    const snapshotLanguage = read_valid_offer_language(offerDraft?.offerContentSnapshot?.offerLanguage);

    if (snapshotLanguage.length) {
        return snapshotLanguage;
    }

    const typesLanguage = read_valid_offer_language(typesData?.offerLanguage);

    if (typesLanguage.length) {
        return typesLanguage;
    }

    return 'el';
};

const offer_created_date = (offerDraft = {}, dateLocale = 'el-GR') => {
    const createdAt = `${offerDraft?.createdAt || ''}`.trim();

    if (!createdAt.length) {
        return '—';
    }

    const dateObject = new Date(createdAt);

    if (Number.isNaN(dateObject.getTime())) {
        return '—';
    }

    return dateObject.toLocaleDateString(dateLocale);
};

const preview_copy_by_language = {
    el: {
        financialOffer: 'Οικονομική Προσφορά',
        service: 'Υπηρεσία',
        agreementTitle: 'Συμφωνητικό συνεργασίας',
        agreementBetween: 'Μεταξύ των:',
        providerLine: 'DO IT FOR ME IKE, με έδρα Μικρασιατών 75, ΑΦΜ EL801134782, εφεξής καλούμενη “Πάροχος”, και',
        providerUndertaking: 'Ο Πάροχος αναλαμβάνει να υλοποιήσει και να παραδώσει το κάτωθεν έργο για τον Πελάτη.',
        clientLegalWithVat: (clientName, clientVatNumber) => `${clientName}, ΑΦΜ ${clientVatNumber}, εφεξής καλούμενη “Πελάτης”.`,
        clientLegalWithoutVat: (clientName) => `${clientName}, εφεξής καλούμενη “Πελάτης”.`,
        brandOverview: 'Επισκόπηση',
        project: 'Project',
        serviceComment: 'Σχόλιο υπηρεσίας',
        hiddenMessage: 'Hidden message',
        questionnaireAnswers: 'Website questionnaire',
        missingQuestionnaireAnswer: '—',
        digitalChannels: 'Ψηφιακά κανάλια επικοινωνίας',
        socialManagement: 'Διαχείριση Facebook & Instagram',
        contentFlow: 'Τρόπος διαχείρισης περιεχομένου',
        recommendedBudget: (budget) => `Προτεινόμενη διαφημιστική δαπάνη: ${budget}`,
        prerequisites: 'Προαπαιτούμενα',
        assumptions: 'Παραδοχές',
        outOfScope: 'Εκτός ορίων έργου',
        finalTerms: 'Τελικές διατάξεις',
        importantInfo: 'Σημαντικές πληροφορίες',
        pricingCatalog: 'Αναλυτικός τιμοκατάλογος υπηρεσιών',
        recurringServices: 'Συνδρομή υπηρεσιών',
        oneOffSetupCost: 'Κόστος έναρξης',
        serviceColumn: 'Υπηρεσία',
        catalogPriceColumn: 'Τιμοκατάλογος',
        discountPercentColumn: 'Έκπτωση %',
        discountValueColumn: 'Έκπτωση €',
        finalValueColumn: 'Τελική αξία',
        packageBenefit: (discountValue, discountPercent) => `Συνολικό όφελος πακέτου: ${discountValue} (${discountPercent}%)`,
        offerPricing: 'Οικονομική προσφορά',
        linkedStripeSubscriptions: 'Συνδεδεμένες συνδρομές Stripe',
        linkedManualBilling: 'Κοινές επαναλαμβανόμενες πληρωμές',
        linkedStripePayments: 'Οι τιμές και οι πληρωμές προέρχονται από τις συνδεδεμένες συνδρομές Stripe.',
        siblingOfferPayments: 'Οι πληρωμές και η ακύρωση ακολουθούν την αρχική προσφορά. Δεν υπάρχει δεύτερη χρέωση.',
        netAmount: 'Καθαρή αξία',
        vatAmount: 'Φόρος προστιθέμενης αξίας',
        paymentMethod: 'Μέθοδος πληρωμής',
        totalWithVat: 'Σύνολο με ΦΠΑ',
        recurringSuffix: '/ 30 ημέρες',
        validityNote: (days) => `Η παρούσα οικονομική προσφορά ισχύει για ${days} ημέρες.`,
        paymentLinkMethod: 'Payment link',
        bankTransferMethod: 'Bank transfer',
        paymentTermsSectionTitle: 'Πληρωμές ανά κατηγορία',
        recurringPaymentPoint: 'a. Η συνδρομή υπηρεσιών ενεργοποιείται με την ολοκλήρωση της πληρωμής της.',
        oneOffPaymentPoint: 'b. Το κόστος έναρξης εξοφλείται εφάπαξ και δεν ανανεώνεται αυτόματα.',
        slogan: 'Revolutionizing the digital marketing industry',
        supportLine: 'Πάντα στη διάθεσή σας για οποιαδήποτε διευκρίνιση.',
        dateLocale: 'el-GR'
    },
    en: {
        financialOffer: 'Financial Offer',
        service: 'Service',
        agreementTitle: 'Service Agreement',
        agreementBetween: 'Between:',
        providerLine: 'DO IT FOR ME IKE, registered at Mikrasiaton 75, VAT EL801134782, hereinafter referred to as the “Provider”, and',
        providerUndertaking: 'The Provider undertakes to deliver the project below for the Client.',
        clientLegalWithVat: (clientName, clientVatNumber) => `${clientName}, VAT ${clientVatNumber}, hereinafter referred to as the “Client”.`,
        clientLegalWithoutVat: (clientName) => `${clientName}, hereinafter referred to as the “Client”.`,
        brandOverview: 'Overview',
        project: 'Project',
        serviceComment: 'Service comment',
        hiddenMessage: 'Hidden message',
        questionnaireAnswers: 'Website questionnaire',
        missingQuestionnaireAnswer: '—',
        digitalChannels: 'Digital Communication Channels',
        socialManagement: 'Facebook & Instagram Management',
        contentFlow: 'Content Management Workflow',
        recommendedBudget: (budget) => `Recommended ad budget: ${budget}`,
        prerequisites: 'Prerequisites',
        assumptions: 'Assumptions',
        outOfScope: 'Out of Scope',
        finalTerms: 'Final Terms',
        importantInfo: 'Important Information',
        pricingCatalog: 'Detailed Service Catalog',
        recurringServices: 'Recurring Services',
        oneOffSetupCost: 'One-off Setup Cost',
        serviceColumn: 'Service',
        catalogPriceColumn: 'Catalog Price',
        discountPercentColumn: 'Discount %',
        discountValueColumn: 'Discount €',
        finalValueColumn: 'Final Value',
        packageBenefit: (discountValue, discountPercent) => `Total package benefit: ${discountValue} (${discountPercent}%)`,
        offerPricing: 'Offer Pricing',
        linkedStripeSubscriptions: 'Linked Stripe subscriptions',
        linkedManualBilling: 'Shared recurring payments',
        linkedStripePayments: 'Pricing and payments come from the linked Stripe subscriptions.',
        siblingOfferPayments: 'Payments and cancellation follow the source Offer. There is no second charge.',
        netAmount: 'Net Amount',
        vatAmount: 'VAT Amount',
        paymentMethod: 'Payment Method',
        totalWithVat: 'Total incl. VAT',
        recurringSuffix: '/ 30 days',
        validityNote: (days) => `This financial offer is valid for ${days} days.`,
        paymentLinkMethod: 'Payment link',
        bankTransferMethod: 'Bank transfer',
        paymentTermsSectionTitle: 'Payment Terms by Category',
        recurringPaymentPoint: 'a. Recurring service activation is completed after recurring payment completion.',
        oneOffPaymentPoint: 'b. One-off setup cost is paid once and does not auto-renew.',
        slogan: 'Revolutionizing the digital marketing industry',
        supportLine: 'We remain available for any clarification.',
        dateLocale: 'en-GB'
    }
};

export default function New_Offer_Preview_Page(props) {
    const { t } = useTranslation();
    const [pendingPriceDifferenceContent, setPendingPriceDifferenceContent] = useState(null);
    const user = useSelector((state) => state.user.user || {});
    const originalDraft = props.offerDraft || {};
    const offerDraft = props.fakeOffer ? { ...originalDraft, setupData: { ...originalDraft.setupData,
        servicePriceOverwriteValues: Object.fromEntries(Object.keys(originalDraft.setupData?.selectedServices || {}).map(key => [key, 0])) },
        priceData: { ...Object.fromEntries(Object.entries(originalDraft.priceData || {}).map(([key, value]) => [key, typeof value === 'number' ? 0 : value])),
            serviceCatalogByType: [], total: 0, totalWithVat: 0, totalNetAmount: 0, subtotal: 0, recurringNetAmount: 0, oneOffNetAmount: 0,
            recurringTotalWithVat: 0, oneOffTotalWithVat: 0, mixedRecurringManualNetAmount: 0, mixedOneOffManualNetAmount: 0, vatEnabled: false } } : originalDraft;
    const setupData = offerDraft?.setupData || {};
    const fakeOfferDetails = setupData?.fakeOfferDetails || {};
    const usesSiblingOffer = fakeOfferDetails.paymentSource === 'linked_offer';
    const usesSharedManualBilling = usesSiblingOffer && fakeOfferDetails.sourcePaymentSource === 'manual';
    const usesStripeSubscriptions = fakeOfferDetails.paymentSource === 'stripe_subscriptions' || usesSiblingOffer;
    const typesData = offerDraft?.typesData || {};
    const offerLanguage = resolve_offer_language(offerDraft, typesData);
    const copy = preview_copy_by_language[offerLanguage] || preview_copy_by_language.el;
    const selectedContentMap = offerLanguage === 'en' ? industry_offer_content_en : industry_offer_content;
    const priceData = offerDraft?.priceData || {};
    const offerCode = offer_reference_from_offer(offerDraft) || offer.code || 'OFR-1908';
    const clientEmails = offer_emails_from_offer(offerDraft);
    const clientPrimaryEmail = clientEmails.length ? clientEmails[0] : '';
    const clientName = offer_client_name_from_offer(offerDraft) || clientPrimaryEmail || 'Client';
    const onlineOfferUrl = `${props.onlineOfferUrl || ''}`.trim();
    const invoiceEnabled = offerDraft?.invoice === false || typesData?.invoiceEnabled === false ? false : true;
    const clientVatNumber = `${offerDraft?.client?.vat || typesData?.clientVatNumber || ''}`.trim();
    const clientLegalLine = invoiceEnabled && clientVatNumber.length
        ? copy.clientLegalWithVat(clientName, clientVatNumber)
        : copy.clientLegalWithoutVat(clientName);
    const previewDate = offer_created_date(offerDraft, copy.dateLocale);
    const selectedTypes = selected_types_from_offer(offerDraft).map(offer_catalog_type_key);
    const showInternalNotes = props.showInternalNotes ?? !props.hideActions;
    const showQuestionnaireAnswers = checkPermissions('Scout', user?.roles);
    const serviceItems = selected_services(offerDraft);
    const reviewerSuggestedPricingValues = setupData?.serviceSuggestedPricingValues || {};
    const reviewerSuggestedPricingRows = serviceItems.reduce((rows, serviceItem) => {
        const rawValue = reviewerSuggestedPricingValues[serviceItem.serviceKey];
        const numericValue = Number(rawValue);
        const finalValue = Number(serviceItem.servicePrice);

        if (`${rawValue ?? ''}`.trim() === '' || !Number.isFinite(numericValue) || numericValue <= 0 || !Number.isFinite(finalValue)) {
            return rows;
        }

        const formatPrice = (value) => value.toLocaleString(offerLanguage === 'en' ? 'en-GB' : 'el-GR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });

        return [
            ...rows,
            {
                serviceKey: serviceItem.serviceKey,
                serviceText: serviceItem.serviceText,
                suggestedValue: formatPrice(numericValue),
                finalValue: formatPrice(finalValue),
                different: Math.abs(numericValue - finalValue) >= 0.01
            }
        ];
    }, []);
    const reviewerPriceDifferences = reviewerSuggestedPricingRows.filter((rowItem) => rowItem.different);
    const serviceComments = setupData?.serviceComments || {};
    const commentByServiceKey = serviceItems.reduce((commentLookup, serviceItem) => {
        const commentValue = rich_text_comment_html_for_preview(serviceComments[serviceItem.serviceKey] || '');
        const normalizedServiceKey = normalize_service_key(serviceItem.serviceKey);

        if (!rich_text_has_content(commentValue)) {
            return commentLookup;
        }

        return {
            ...commentLookup,
            [normalizedServiceKey]: commentValue
        };
    }, {});
    const serviceHiddenMessages = setupData?.serviceHiddenMessages || {};
    const hiddenMessageByServiceKey = serviceItems.reduce((hiddenMessageLookup, serviceItem) => {
        const hiddenMessageValue = `${serviceHiddenMessages[serviceItem.serviceKey] || ''}`.trim();
        const normalizedServiceKey = normalize_service_key(serviceItem.serviceKey);

        if (!hiddenMessageValue.length) {
            return hiddenMessageLookup;
        }

        return {
            ...hiddenMessageLookup,
            [normalizedServiceKey]: hiddenMessageValue
        };
    }, {});
    const selectedServiceKeys = serviceItems.map((service) => normalize_service_key(service.serviceKey));
    const hasEshopService = selectedServiceKeys.includes('eshop') || selectedServiceKeys.includes('plan_eshop');
    const eshopProjectText = `${setupData?.eshopProjectText || ''}`.trim();
    const eshopProjectTextHtml = rich_text_comment_html_for_preview(eshopProjectText);
    const questionnaireAnswersByType = {
        starter: setupData?.starterQuestionAnswers || {},
        business: setupData?.businessQuestionAnswers || {},
        eshop: setupData?.eshopQuestionAnswers || {}
    };
    const hasEshopQuestionnaireAnswers = showQuestionnaireAnswers && questionnaire_rows(
        'eshop',
        questionnaireAnswersByType.eshop,
        copy.missingQuestionnaireAnswer
    ).length > 0;
    const availableServices = available_services_for_selected_types(selectedTypes);
    const unselectedServiceNames = availableServices
        .filter((service) => !selectedServiceKeys.includes(service.serviceKey))
        .map((service) => service.serviceText);
    const normalizedIndustryKey = (typesData?.category || typesData?.industry || '').trim().toLowerCase();
    const contentKey = report_content_key(selectedTypes, normalizedIndustryKey);
    const storedContentSnapshot = offerDraft?.offerContentSnapshot || {};
    const storedPricingSnapshot = storedContentSnapshot?.pricing || {};
    const hasStoredContentSnapshot = !!storedContentSnapshot?.reportData;
    const dynamicReportData = build_offer_report({
        industryKey: normalizedIndustryKey,
        contentKey,
        selectedServiceKeys,
        unselectedServiceNames,
        offerLanguage,
        contentMap: selectedContentMap
    });
    const reportData = hasStoredContentSnapshot
        ? (storedContentSnapshot?.reportData || dynamicReportData)
        : dynamicReportData;
    const hasSavedPriceAmounts = price_data_has_saved_amounts(priceData);
    const storedServiceCatalogByType = Array.isArray(storedPricingSnapshot?.serviceCatalogByType)
        ? storedPricingSnapshot.serviceCatalogByType
        : Array.isArray(priceData?.serviceCatalogByType)
            ? priceData.serviceCatalogByType
            : [];
    const calculatedServiceCatalogByType = type_pricing_catalog(serviceItems, priceData);
    const serviceCatalogByType = storedServiceCatalogByType.length
        ? storedServiceCatalogByType
        : props.hideActions && hasSavedPriceAmounts
            ? []
            : calculatedServiceCatalogByType;
    const serviceSummary = serviceItems.length
        ? serviceItems.map((service) => service.serviceText).join(', ')
        : '—';
    const serviceSummaryPrefix = hasStoredContentSnapshot
        ? (storedContentSnapshot?.serviceSummaryPrefix || service_summary_prefix(selectedTypes, offerLanguage))
        : service_summary_prefix(selectedTypes, offerLanguage);
    const dynamicServiceSummaryText = serviceItems.length
        ? `${serviceSummaryPrefix}: ${serviceSummary}`
        : serviceSummaryPrefix;
    const serviceSummaryText = hasStoredContentSnapshot
        ? (storedContentSnapshot?.serviceSummaryText || dynamicServiceSummaryText)
        : dynamicServiceSummaryText;
    const postsPerMonth = social_posts_per_month(offerDraft);
    const socialManagementItems = hasStoredContentSnapshot
        ? (reportData?.socialManagementItems || [])
        : reportData.socialManagementItems.map((socialManagementItem) => {
            if (/δημοσιεύσεις \/ 30 ημέρες βάσει προγράμματος\./i.test(socialManagementItem)) {
                return `${postsPerMonth} δημοσιεύσεις / 30 ημέρες βάσει προγράμματος.`;
            }

            if (/posts \/ 30 days based on plan\./i.test(socialManagementItem)) {
                return `${postsPerMonth} posts / 30 days based on plan.`;
            }

            return socialManagementItem;
        });
    const noOverview = offerDraft?.setupData?.noOverview === true;
    const brandOverview = noOverview ? '' : `${priceData?.brandOverview || ''}`.trim();
    const brandOverviewHtml = rich_text_comment_html_for_preview(brandOverview);
    const overviewHiddenMessage = `${priceData?.overviewHiddenMessage || ''}`.trim();
    const vatRate = priceData?.vatEnabled === false ? 0 : Number(priceData?.vatRate ?? offer.vat ?? 0);
    const pricedServiceItems = serviceItems.map(to_priced_service_item);
    const calculatedTotals = calculate_offer_totals({
        pricedServices: pricedServiceItems,
        typeDiscountValues: priceData?.typeDiscountValues || {},
        vatEnabled: priceData?.vatEnabled ?? true,
        vatRate
    });
    const savedTotalWithVat = first_price_number(priceData?.total, priceData?.totalWithVat);
    const savedTotalNetAmount = savedTotalWithVat === null ? null : net_amount_from_total(savedTotalWithVat, vatRate);
    const savedTotalVatAmount = savedTotalWithVat === null ? null : vat_amount_from_total(savedTotalWithVat, vatRate);
    const hasServiceItems = serviceItems.length > 0;
    const detectedHasRecurringPayments = serviceItems.some((service) => !service.serviceOneOff);
    const detectedHasOneOffServices = serviceItems.some((service) => service.serviceOneOff);
    const oneOffServicesAreFree = detectedHasRecurringPayments && detectedHasOneOffServices;
    const detectedHasOneOffPayments = detectedHasOneOffServices && !oneOffServicesAreFree;
    const savedRecurringPaymentAmount = first_price_number(
        priceData?.recurringTotalWithVat,
        priceData?.recurringNetAmount,
        priceData?.mixedRecurringManualNetAmount
    );
    const savedOneOffPaymentAmount = first_price_number(
        priceData?.oneOffTotalWithVat,
        priceData?.oneOffNetAmount,
        priceData?.mixedOneOffManualNetAmount
    );
    const savedOneOffPaymentConfigured = `${priceData?.oneOffPaymentLinkUrl || priceData?.oneOffPaymentMethod || ''}`.trim().length > 0
        || priceData?.oneOffPaymentUsesBankTransfer === true;
    const savedHasRecurringPayments = priceData?.hasRecurringServices ?? savedRecurringPaymentAmount > 0;
    const savedHasOneOffPayments = priceData?.hasOneOffServices ?? savedOneOffPaymentAmount > 0;
    const savedHasPaymentSplit = savedHasRecurringPayments || savedHasOneOffPayments;
    const hasRecurringPayments = hasSavedPriceAmounts
        ? savedHasRecurringPayments || (!savedHasPaymentSplit && !savedOneOffPaymentConfigured)
        : hasServiceItems
            ? detectedHasRecurringPayments
            : priceData?.hasRecurringServices ?? calculatedTotals.hasRecurringServices;
    const hasOneOffPayments = hasSavedPriceAmounts
        ? savedHasOneOffPayments || (!savedHasPaymentSplit && savedOneOffPaymentConfigured)
        : hasServiceItems
            ? detectedHasOneOffPayments
            : priceData?.hasOneOffServices ?? calculatedTotals.hasOneOffServices;
    const hasMixedPayments = hasRecurringPayments && hasOneOffPayments;
    const mixedRecurringManualNetAmount = price_number_or_null(priceData?.mixedRecurringManualNetAmount);
    const mixedOneOffManualNetAmount = price_number_or_null(priceData?.mixedOneOffManualNetAmount);
    const hasMixedRecurringManualNetAmount = hasMixedPayments
        && Number.isFinite(mixedRecurringManualNetAmount)
        && mixedRecurringManualNetAmount >= 0;
    const hasMixedOneOffManualNetAmount = hasMixedPayments
        && Number.isFinite(mixedOneOffManualNetAmount)
        && mixedOneOffManualNetAmount >= 0;
    const hasMixedManualNetAmounts = hasMixedRecurringManualNetAmount || hasMixedOneOffManualNetAmount;
    const recurringBaseSubtotalByPayment = hasServiceItems
        ? calculatedTotals.recurringNetAmount
        : Number.isFinite(Number(priceData?.mixedRecurringBaseNetAmount))
            ? Number(priceData.mixedRecurringBaseNetAmount)
            : Number.isFinite(Number(priceData?.recurringNetAmount))
                ? Number(priceData.recurringNetAmount)
                : 0;
    const oneOffBaseSubtotalByPayment = hasServiceItems
        ? calculatedTotals.oneOffNetAmount
        : Number.isFinite(Number(priceData?.mixedOneOffBaseNetAmount))
            ? Number(priceData.mixedOneOffBaseNetAmount)
            : Number.isFinite(Number(priceData?.oneOffNetAmount))
                ? Number(priceData.oneOffNetAmount)
                : 0;
    const calculatedSubtotal = hasMixedPayments
        ? round_money(recurringBaseSubtotalByPayment + oneOffBaseSubtotalByPayment)
        : hasServiceItems
            ? calculatedTotals.subtotalBeforeDiscount
            : 0;
    const subtotal = first_price_number(priceData?.subtotal, calculatedSubtotal) || 0;
    const calculatedRecurringSubtotalWithoutVat = hasMixedManualNetAmounts
        ? (hasMixedRecurringManualNetAmount ? mixedRecurringManualNetAmount : recurringBaseSubtotalByPayment)
        : hasServiceItems
            ? calculatedTotals.recurringNetAmount
            : 0;
    const recurringSubtotalWithoutVat = first_price_number(
        priceData?.mixedRecurringManualNetAmount,
        priceData?.recurringNetAmount,
        calculatedRecurringSubtotalWithoutVat
    ) || 0;
    const calculatedOneOffSubtotalWithoutVat = hasMixedManualNetAmounts
        ? (hasMixedOneOffManualNetAmount ? mixedOneOffManualNetAmount : oneOffBaseSubtotalByPayment)
        : hasServiceItems
            ? calculatedTotals.oneOffNetAmount
            : 0;
    const oneOffSubtotalWithoutVat = first_price_number(
        priceData?.mixedOneOffManualNetAmount,
        priceData?.oneOffNetAmount,
        calculatedOneOffSubtotalWithoutVat
    ) || 0;
    const totalWithoutVat = first_price_number(
        priceData?.totalNetAmount,
        savedTotalNetAmount,
        round_money(recurringSubtotalWithoutVat + oneOffSubtotalWithoutVat)
    ) || 0;
    const calculatedRecurringVatAmount = hasMixedManualNetAmounts
        ? round_money(recurringSubtotalWithoutVat * vatRate)
        : hasServiceItems
            ? calculatedTotals.recurringVatAmount
            : round_money(recurringSubtotalWithoutVat * vatRate);
    const recurringVatAmount = first_price_number(priceData?.recurringVatAmount, calculatedRecurringVatAmount) || 0;
    const calculatedOneOffVatAmount = hasMixedManualNetAmounts
        ? round_money(oneOffSubtotalWithoutVat * vatRate)
        : hasServiceItems
            ? calculatedTotals.oneOffVatAmount
            : round_money(oneOffSubtotalWithoutVat * vatRate);
    const oneOffVatAmount = first_price_number(priceData?.oneOffVatAmount, calculatedOneOffVatAmount) || 0;
    const calculatedVatAmount = hasMixedManualNetAmounts
        ? round_money(recurringVatAmount + oneOffVatAmount)
        : hasServiceItems
            ? calculatedTotals.totalVatAmount
            : round_money(recurringVatAmount + oneOffVatAmount);
    const vatAmount = first_price_number(priceData?.totalVatAmount, savedTotalVatAmount, calculatedVatAmount) || 0;
    const calculatedRecurringTotalWithVat = hasMixedManualNetAmounts
        ? round_money(recurringSubtotalWithoutVat + recurringVatAmount)
        : hasServiceItems
            ? calculatedTotals.recurringTotalWithVat
            : round_money(recurringSubtotalWithoutVat + recurringVatAmount);
    const recurringTotalWithVat = first_price_number(priceData?.recurringTotalWithVat, calculatedRecurringTotalWithVat) || 0;
    const calculatedOneOffTotalWithVat = hasMixedManualNetAmounts
        ? round_money(oneOffSubtotalWithoutVat + oneOffVatAmount)
        : hasServiceItems
            ? calculatedTotals.oneOffTotalWithVat
            : round_money(oneOffSubtotalWithoutVat + oneOffVatAmount);
    const oneOffTotalWithVat = first_price_number(priceData?.oneOffTotalWithVat, calculatedOneOffTotalWithVat) || 0;
    const calculatedTotalWithVat = hasMixedManualNetAmounts
        ? round_money(recurringTotalWithVat + oneOffTotalWithVat)
        : hasServiceItems
            ? calculatedTotals.totalWithVat
            : round_money(recurringTotalWithVat + oneOffTotalWithVat);
    const totalWithVat = first_price_number(savedTotalWithVat, calculatedTotalWithVat) || 0;
    const validFor = Number(priceData?.validFor ?? offer.valid_for ?? 30);
    const finalTermsByPaymentMode = (reportData?.finalTerms || [])
        .map((termItem) => {
            const nextPoints = hasRecurringPayments
                ? termItem.points || []
                : (termItem.points || []).filter((pointItem) => {
                    return !/συνδρομ|ανανεώνεται|recurring/i.test(pointItem);
                });

            return {
                ...termItem,
                points: nextPoints
            };
        })
        .filter((termItem) => termItem.points.length);
    const paymentSpecificPoints = [
        ...(hasRecurringPayments ? [copy.recurringPaymentPoint] : []),
        ...(hasOneOffPayments ? [copy.oneOffPaymentPoint] : [])
    ];
    const finalTermsItems = paymentSpecificPoints.length
        ? [
            ...finalTermsByPaymentMode,
            {
                title: copy.paymentTermsSectionTitle,
                points: paymentSpecificPoints
            }
        ]
        : finalTermsByPaymentMode;
    const recurringPaymentLinkUrl = `${priceData?.recurringPaymentLinkUrl || priceData?.paymentLinkUrl || ''}`.trim();
    const oneOffPaymentLinkUrl = `${priceData?.oneOffPaymentLinkUrl || ''}`.trim();
    const recurringUsesBankTransfer = priceData?.recurringPaymentUsesBankTransfer === true
        || `${priceData?.recurringPaymentMethod || ''}`.trim().toLowerCase() === 'bank_transfer'
        || (hasRecurringPayments && !recurringPaymentLinkUrl.length);
    const oneOffUsesBankTransfer = priceData?.oneOffPaymentUsesBankTransfer === true
        || `${priceData?.oneOffPaymentMethod || ''}`.trim().toLowerCase() === 'bank_transfer'
        || (hasOneOffPayments && !oneOffPaymentLinkUrl.length);
    const recurringPaymentMethodLabel = recurringUsesBankTransfer ? copy.bankTransferMethod : copy.paymentLinkMethod;
    const oneOffPaymentMethodLabel = oneOffUsesBankTransfer ? copy.bankTransferMethod : copy.paymentLinkMethod;
    const recurringCatalogSubtotal = hasMixedPayments ? catalog_subtotal_by_payment(serviceCatalogByType, 'recurring') : 0;
    const oneOffCatalogSubtotal = hasMixedPayments ? catalog_subtotal_by_payment(serviceCatalogByType, 'one_off') : 0;
    const recurringCatalogDiscountPercentOverride = hasMixedRecurringManualNetAmount
        ? payment_discount_percent_by_manual(recurringCatalogSubtotal, recurringSubtotalWithoutVat)
        : null;
    const oneOffCatalogDiscountPercentOverride = hasMixedOneOffManualNetAmount
        ? payment_discount_percent_by_manual(oneOffCatalogSubtotal, oneOffSubtotalWithoutVat)
        : null;
    const recurringCatalogByType = hasMixedPayments
        ? type_pricing_catalog_by_payment(serviceCatalogByType, 'recurring', recurringCatalogDiscountPercentOverride)
        : [];
    const oneOffCatalogByType = hasMixedPayments
        ? type_pricing_catalog_by_payment(serviceCatalogByType, 'one_off', oneOffCatalogDiscountPercentOverride)
        : [];
    const savedSingleRecurringTotal = hasSavedPriceAmounts && savedTotalWithVat !== null && hasRecurringPayments && !hasOneOffPayments && !hasMixedPayments;
    const savedSingleOneOffTotal = hasSavedPriceAmounts && savedTotalWithVat !== null && hasOneOffPayments && !hasRecurringPayments && !hasMixedPayments;
    const recurringNetValue = hasRecurringPayments
        ? first_price_number(
            priceData?.mixedRecurringManualNetAmount,
            priceData?.recurringNetAmount,
            savedSingleRecurringTotal ? savedTotalNetAmount : null,
            recurringSubtotalWithoutVat
        ) || 0
        : 0;
    const oneOffNetValue = hasOneOffPayments
        ? first_price_number(
            priceData?.mixedOneOffManualNetAmount,
            priceData?.oneOffNetAmount,
            savedSingleOneOffTotal ? savedTotalNetAmount : null,
            oneOffSubtotalWithoutVat
        ) || 0
        : 0;
    const recurringVatValue = hasRecurringPayments
        ? first_price_number(
            priceData?.recurringVatAmount,
            savedSingleRecurringTotal ? savedTotalVatAmount : null,
            recurringVatAmount
        ) || 0
        : 0;
    const oneOffVatValue = hasOneOffPayments
        ? first_price_number(
            priceData?.oneOffVatAmount,
            savedSingleOneOffTotal ? savedTotalVatAmount : null,
            oneOffVatAmount
        ) || 0
        : 0;
    const recurringTotalValue = hasRecurringPayments
        ? first_price_number(
            savedSingleRecurringTotal ? savedTotalWithVat : null,
            priceData?.recurringTotalWithVat,
            recurringTotalWithVat
        ) || 0
        : 0;
    const oneOffTotalValue = hasOneOffPayments
        ? first_price_number(
            savedSingleOneOffTotal ? savedTotalWithVat : null,
            priceData?.oneOffTotalWithVat,
            oneOffTotalWithVat
        ) || 0
        : 0;
    const showRecurringPricing = hasRecurringPayments && recurringTotalValue > 0;
    const showOneOffPricing = hasOneOffPayments && oneOffTotalValue > 0;
    const showAnyPricing = showRecurringPricing || showOneOffPricing;
    const showMixedPricing = showRecurringPricing && showOneOffPricing;
    const singlePricingIsRecurring = showRecurringPricing;
    const singlePricingNetValue = singlePricingIsRecurring ? recurringNetValue : oneOffNetValue;
    const singlePricingVatValue = singlePricingIsRecurring ? recurringVatValue : oneOffVatValue;
    const singlePricingTotalValue = singlePricingIsRecurring ? recurringTotalValue : oneOffTotalValue;
    const singlePricingMethodLabel = singlePricingIsRecurring ? recurringPaymentMethodLabel : oneOffPaymentMethodLabel;
    const reportServiceSections = reportData?.serviceSections || [];
    const reportServiceKeys = reportServiceSections.flatMap((serviceSection) => serviceSection?.serviceKeys || []);
    const customServiceSections = serviceItems
        .filter((serviceItem) => serviceItem.serviceKey.startsWith('website_creation_custom_'))
        .map((serviceItem) => ({
            serviceKeys: [normalize_service_key(serviceItem.serviceKey)],
            title: serviceItem.serviceText,
            items: []
        }))
        .filter((serviceSection) => !reportServiceKeys.includes(serviceSection.serviceKeys[0]));
    const serviceSectionsForRender = [...reportServiceSections, ...customServiceSections].map((serviceSection) => {
        if (hasStoredContentSnapshot) {
            return {
                ...serviceSection,
                recommendedBudget: serviceSection?.recommendedBudget || ''
            };
        }

        return {
            ...serviceSection,
            recommendedBudget: read_service_section_budget(offerDraft, serviceSection, serviceItems, offerLanguage)
        };
    });
    const hasEshopQuestionnaireServiceSection = serviceSectionsForRender.some((serviceSection) => {
        return questionnaire_type_from_service_keys(serviceSection.serviceKeys || []) === 'eshop';
    });
    const storedFinalTermsItems = hasStoredContentSnapshot
        ? (storedContentSnapshot?.finalTermsItems || [])
        : [];
    const finalTermsRenderItems = storedFinalTermsItems.length ? storedFinalTermsItems : finalTermsItems;
    const offerContentTemplateKey = `${contentKey || normalizedIndustryKey || 'general'}_${offerLanguage}`.trim() || `general_${offerLanguage}`;
    const offerContentTemplateFingerprint = offer_content_template_fingerprint(normalizedIndustryKey, contentKey, selectedContentMap);
    const offerContentSnapshot = {
        templateKey: offerContentTemplateKey,
        offerLanguage,
        capturedAt: new Date().toISOString(),
        serviceSummaryPrefix,
        serviceSummaryText,
        reportData: {
            ...reportData,
            socialManagementItems,
            serviceSections: serviceSectionsForRender
        },
        pricing: {
            serviceCatalogByType,
            hasRecurringPayments,
            hasOneOffPayments,
            hasMixedPayments,
            recurringNetValue,
            oneOffNetValue,
            recurringVatValue,
            oneOffVatValue,
            recurringTotalValue,
            oneOffTotalValue,
            vatAmount,
            totalWithoutVat,
            totalWithVat
        },
        finalTermsItems: finalTermsRenderItems
    };
    const render_service_comment = (commentValue = '', keyValue = '') => {
        const normalizedComment = `${commentValue || ''}`.trim();

        if (!rich_text_has_content(normalizedComment)) {
            return null;
        }

        return (
            <NewOfferPreviewRichText
                key={keyValue}
                dangerouslySetInnerHTML={{
                    __html: normalizedComment
                }}
            />
        );
    };
    const render_questionnaire_answers = (questionnaireType = '', keyValue = '') => {
        if (!showQuestionnaireAnswers) {
            return null;
        }

        const rows = questionnaire_rows(
            questionnaireType,
            questionnaireAnswersByType[questionnaireType] || {},
            copy.missingQuestionnaireAnswer
        );

        if (!rows.length) {
            return null;
        }

        return (
            <NewOfferPreviewQuestionnaire key={keyValue}>
                <h6>{copy.questionnaireAnswers}</h6>
                <NewOfferPreviewQuestionnaireRows>
                    {rows.map((rowItem) => (
                        <NewOfferPreviewQuestionnaireRow key={`${keyValue}_${rowItem.question}`}>
                            <h6>{rowItem.question}</h6>
                            <p>{rowItem.answer}</p>
                        </NewOfferPreviewQuestionnaireRow>
                    ))}
                </NewOfferPreviewQuestionnaireRows>
            </NewOfferPreviewQuestionnaire>
        );
    };
    const create_and_send = (replacementConfirmation = null) => {
        if (!props.onCreateAndSend) {
            return;
        }

        const offerCreationContent = {
            replacementConfirmation,
            offerContentSnapshot,
            offerContentTemplateKey,
            offerContentTemplateFingerprint
        };

        if (!props.fakeOffer && props.confirmReviewerPriceDifference && reviewerPriceDifferences.length) {
            setPendingPriceDifferenceContent(offerCreationContent);
            return;
        }

        props.onCreateAndSend(offerCreationContent);
    };
    const render_reviewer_suggested_pricing_note = (keyValue) => {
        if (!props.showReviewerSuggestedPricingNote || !reviewerSuggestedPricingRows.length) {
            return null;
        }

        return (
            <NewOfferPreviewReviewerNote
                key={keyValue}
                role="alert"
                aria-label={t('Important reviewer pricing comparison')}
                $hasDifferences={reviewerPriceDifferences.length > 0}
            >
                <h3>{t('IMPORTANT — Reviewer suggested prices')}</h3>
                <p>{t('Reviewer suggestions are service prices before VAT. Apply them to recalculate the offer, or explicitly keep your current prices.')}</p>
                <ul>
                    {reviewerSuggestedPricingRows.map((rowItem) => (
                        <NewOfferPreviewReviewerPricingRow
                            key={rowItem.serviceKey}
                            $different={rowItem.different}
                        >
                            <span>{rowItem.serviceText}</span>
                            <span>{`${t('Suggested price')}: € ${rowItem.suggestedValue}`}</span>
                            <span>{`${t('Final offer price')}: € ${rowItem.finalValue}`}</span>
                            <strong>{t(rowItem.different ? 'PRICE CHANGED' : 'Matches suggestion')}</strong>
                        </NewOfferPreviewReviewerPricingRow>
                    ))}
                </ul>
                {reviewerPriceDifferences.length > 0 && props.confirmReviewerPriceDifference && props.onApplyReviewerPrices ? (
                    <Main_ text={t('Use reviewer prices')} disabled={props.isSaving} onClick={props.onApplyReviewerPrices}></Main_>
                ) : null}
            </NewOfferPreviewReviewerNote>
        );
    };
    const priceSummaryProps = {
        netAmount: totalWithoutVat,
        vatAmount,
        totalAmount: totalWithVat,
        hasRecurringPayments,
        hasOneOffPayments,
        recurringAmount: recurringTotalValue,
        oneOffAmount: oneOffTotalValue
    };

    return (
        <NewOfferPreviewPage>
            {pendingPriceDifferenceContent ? (
                <Confirmation_
                    message={t('Your offer prices differ from the reviewer suggestions. Use reviewer prices to recalculate before creating, or keep your current prices and create the offer.')}
                    yes={t('Use reviewer prices')}
                    no={t('Keep current prices and create')}
                    onClose={() => setPendingPriceDifferenceContent(null)}
                    onNo={() => {
                        const offerCreationContent = pendingPriceDifferenceContent;
                        setPendingPriceDifferenceContent(null);
                        props.onCreateAndSend(offerCreationContent);
                    }}
                    onYes={() => {
                        setPendingPriceDifferenceContent(null);
                        props.onApplyReviewerPrices();
                    }}
                />
            ) : null}
            {!props.hideActions ? (
                <NewOfferPreviewActions>
                    <New_Offer_Price_Summary {...priceSummaryProps} />
                    {render_reviewer_suggested_pricing_note('reviewer-pricing-top')}
                </NewOfferPreviewActions>
            ) : null}
            <NewOfferPreviewPaper>
                <NewOfferPreviewCover>
                    <NewOfferPreviewCoverLeft>
                        <NewOfferPreviewLogo>
                            <NewOfferPreviewLogoImage src={companyLogoUrl} alt="Doitforme logo" />
                        </NewOfferPreviewLogo>
                        <NewOfferPreviewBrand>Doitforme</NewOfferPreviewBrand>
                        <NewOfferPreviewTitle>
                            <h2>{copy.financialOffer}</h2>
                            <NewOfferPreviewOfferCode>{offerCode}</NewOfferPreviewOfferCode>
                        </NewOfferPreviewTitle>
                    </NewOfferPreviewCoverLeft>
                </NewOfferPreviewCover>
                <NewOfferPreviewSummary>
                    <NewOfferPreviewSummaryItem>
                        <h4>{copy.service}</h4>
                        <p>{serviceSummaryText}</p>
                        <NewOfferPreviewSummaryMeta>
                            <h6>{previewDate}</h6>
                            <h6>{clientName}</h6>
                        </NewOfferPreviewSummaryMeta>
                    </NewOfferPreviewSummaryItem>
                </NewOfferPreviewSummary>

                <NewOfferPreviewBody>
                    <NewOfferPreviewSection>
                        <h3>{copy.agreementTitle}</h3>
                        <p>{copy.agreementBetween}</p>
                        <p>{copy.providerLine}</p>
                        <p>{clientLegalLine}</p>
                        <p>{copy.providerUndertaking}</p>
                    </NewOfferPreviewSection>

                    {rich_text_has_content(brandOverview) ? (
                        <NewOfferPreviewSection>
                            <h3>{copy.brandOverview}</h3>
                            <NewOfferPreviewRichText dangerouslySetInnerHTML={{ __html: brandOverviewHtml }} />
                        </NewOfferPreviewSection>
                    ) : null}
                    {hasEshopService && (rich_text_has_content(eshopProjectText) || (hasEshopQuestionnaireAnswers && !hasEshopQuestionnaireServiceSection)) ? (
                        <NewOfferPreviewSection>
                            <h3>{copy.project}</h3>
                            {rich_text_has_content(eshopProjectText) ? (
                                <NewOfferPreviewRichText dangerouslySetInnerHTML={{ __html: eshopProjectTextHtml }} />
                            ) : null}
                            {!hasEshopQuestionnaireServiceSection ? render_questionnaire_answers('eshop', 'eshop_questionnaire') : null}
                        </NewOfferPreviewSection>
                    ) : null}
                    {showInternalNotes && overviewHiddenMessage.length ? (
                        <NewOfferPreviewSection>
                            <h3>{copy.hiddenMessage}</h3>
                            <NewOfferPreviewInternalNote>
                                <p>{overviewHiddenMessage}</p>
                            </NewOfferPreviewInternalNote>
                        </NewOfferPreviewSection>
                    ) : null}

                    {reportData.digitalChannels.length ? (
                        <NewOfferPreviewSection>
                            <h3>{copy.digitalChannels}</h3>
                            <NewOfferPreviewList>
                                {reportData.digitalChannels.map((channel) => (
                                    <li key={channel}>
                                        <p>{channel}</p>
                                    </li>
                                ))}
                            </NewOfferPreviewList>
                        </NewOfferPreviewSection>
                    ) : null}

                    {reportData.socialManagementItems.length ? (
                        <NewOfferPreviewSection>
                            <h3>{copy.socialManagement}</h3>
                            <NewOfferPreviewList>
                                {socialManagementItems.map((item) => (
                                    <li key={item}>
                                        <p>{item}</p>
                                    </li>
                                ))}
                            </NewOfferPreviewList>
                            {commentByServiceKey.social_media_management ? (
                                <NewOfferPreviewServiceComment>
                                    <h6>{copy.serviceComment}</h6>
                                    {render_service_comment(commentByServiceKey.social_media_management, 'social_media_management_comment')}
                                </NewOfferPreviewServiceComment>
                            ) : null}
                            {showInternalNotes && hiddenMessageByServiceKey.social_media_management ? (
                                <NewOfferPreviewInternalNote>
                                    <h6>{copy.hiddenMessage}</h6>
                                    <p>{hiddenMessageByServiceKey.social_media_management}</p>
                                </NewOfferPreviewInternalNote>
                            ) : null}
                        </NewOfferPreviewSection>
                    ) : null}

                    {reportData.contentFlowItems.length ? (
                        <NewOfferPreviewSection>
                            <h3>{copy.contentFlow}</h3>
                            <NewOfferPreviewFlow>
                                {reportData.contentFlowItems.map((stepItem, stepIndex) => (
                                    <NewOfferPreviewFlowItem key={stepItem.title}>
                                        <NewOfferPreviewFlowHead>
                                            <NewOfferPreviewFlowIndex>
                                                <h6>{stepIndex + 1}</h6>
                                            </NewOfferPreviewFlowIndex>
                                            <h4>{stepItem.title}</h4>
                                        </NewOfferPreviewFlowHead>
                                        <NewOfferPreviewList>
                                            {stepItem.lines.map((line) => (
                                                <li key={line}>
                                                    <p>{line}</p>
                                                </li>
                                            ))}
                                        </NewOfferPreviewList>
                                    </NewOfferPreviewFlowItem>
                                ))}
                            </NewOfferPreviewFlow>
                        </NewOfferPreviewSection>
                    ) : null}

                    {serviceSectionsForRender.map((serviceSection) => {
                        const sectionQuestionnaireType = questionnaire_type_from_service_keys(serviceSection.serviceKeys || []);
                        const sectionBudget = serviceSection?.recommendedBudget || '';
                        const sectionComments = Array.isArray(serviceSection.serviceKeys)
                            ? serviceSection.serviceKeys
                                .map((serviceKey) => commentByServiceKey[serviceKey] || '')
                                .filter((commentValue) => rich_text_has_content(commentValue))
                            : [];
                        const sectionHiddenMessages = Array.isArray(serviceSection.serviceKeys)
                            ? serviceSection.serviceKeys
                                .map((serviceKey) => hiddenMessageByServiceKey[serviceKey] || '')
                                .filter((hiddenMessageValue) => hiddenMessageValue.length)
                            : [];

                        return (
                            <NewOfferPreviewSection key={serviceSection.title}>
                                <h3>{serviceSection.title}</h3>
                                {serviceSection.items.length ? (
                                    <NewOfferPreviewList>
                                        {serviceSection.items.map((item) => (
                                            <li key={item}>
                                                <p>{item}</p>
                                            </li>
                                        ))}
                                    </NewOfferPreviewList>
                                ) : null}
                                {sectionBudget ? (
                                    <h5>{copy.recommendedBudget(sectionBudget)}</h5>
                                ) : null}
                                {sectionQuestionnaireType ? (
                                    render_questionnaire_answers(sectionQuestionnaireType, `${serviceSection.title}_questionnaire`)
                                ) : null}
                                {sectionComments.length ? (
                                    <NewOfferPreviewServiceComment>
                                        <h6>{copy.serviceComment}</h6>
                                        {sectionComments.map((commentValue, commentIndex) => (
                                            render_service_comment(commentValue, `${serviceSection.title}_comment_${commentIndex}`)
                                        ))}
                                    </NewOfferPreviewServiceComment>
                                ) : null}
                                {showInternalNotes && sectionHiddenMessages.length ? (
                                    <NewOfferPreviewInternalNote>
                                        <h6>{copy.hiddenMessage}</h6>
                                        {sectionHiddenMessages.map((hiddenMessageValue) => (
                                            <p key={hiddenMessageValue}>{hiddenMessageValue}</p>
                                        ))}
                                    </NewOfferPreviewInternalNote>
                                ) : null}
                            </NewOfferPreviewSection>
                        );
                    })}

                    <NewOfferPreviewSection>
                        <h3>{copy.prerequisites}</h3>
                        <NewOfferPreviewList>
                            {reportData.prerequisitesItems.map((item) => (
                                <li key={item}>
                                    <p>{item}</p>
                                </li>
                            ))}
                        </NewOfferPreviewList>
                    </NewOfferPreviewSection>

                    <NewOfferPreviewSection>
                        <h3>{copy.assumptions}</h3>
                        <NewOfferPreviewList>
                            {reportData.assumptionsItems.map((item) => (
                                <li key={item}>
                                    <p>{item}</p>
                                </li>
                            ))}
                        </NewOfferPreviewList>
                    </NewOfferPreviewSection>

                    <NewOfferPreviewSection>
                        <h3>{copy.outOfScope}</h3>
                        <NewOfferPreviewList>
                            {reportData.outOfScopeItems.map((item) => (
                                <li key={item}>
                                    <p>{item}</p>
                                </li>
                            ))}
                        </NewOfferPreviewList>
                    </NewOfferPreviewSection>

                    <NewOfferPreviewSection>
                        <h3>{copy.finalTerms}</h3>
                        <NewOfferPreviewTerms>
                            {finalTermsRenderItems.map((term) => (
                                <NewOfferPreviewTerm key={term.title}>
                                    <h4>{term.title}</h4>
                                    <NewOfferPreviewTermsList>
                                        {term.points.map((point) => (
                                            <li key={point}>
                                                <p>{point}</p>
                                            </li>
                                        ))}
                                    </NewOfferPreviewTermsList>
                                </NewOfferPreviewTerm>
                            ))}
                        </NewOfferPreviewTerms>
                    </NewOfferPreviewSection>

                    <NewOfferPreviewSection>
                        <h3>{copy.importantInfo}</h3>
                        <NewOfferPreviewImportant>
                            {reportData.importantItems.map((item) => (
                                <NewOfferPreviewImportantItem key={item.title}>
                                    <h4>{item.title}</h4>
                                    {item.lines.map((line) => (
                                        <p key={line}>{line}</p>
                                    ))}
                                </NewOfferPreviewImportantItem>
                            ))}
                        </NewOfferPreviewImportant>
                    </NewOfferPreviewSection>

                    {usesStripeSubscriptions ? (
                        <NewOfferPreviewSection>
                            <h3>{usesSharedManualBilling ? copy.linkedManualBilling : copy.linkedStripeSubscriptions}</h3>
                            <p>{usesSiblingOffer ? copy.siblingOfferPayments : copy.linkedStripePayments}</p>
                            {usesSiblingOffer ? <p>{fakeOfferDetails.linkedOfferCode}</p> : null}
                            {usesSharedManualBilling ? <p>{`${fakeOfferDetails.sourcePaymentMethod || '-'} · ${fakeOfferDetails.billingPeriod || '-'}`}</p> : null}
                            {((usesSiblingOffer ? fakeOfferDetails.sharedSubscriptionIds : fakeOfferDetails.subscriptionIds) || []).map((subscriptionId) => (
                                <p key={subscriptionId}>{subscriptionId}</p>
                            ))}
                        </NewOfferPreviewSection>
                    ) : null}

                    {!usesStripeSubscriptions && serviceCatalogByType.length ? (
                        <NewOfferPreviewSection>
                            <h3>{copy.pricingCatalog}</h3>
                            <NewOfferPreviewCatalog>
                                {hasMixedPayments ? (
                                    <>
                                        <NewOfferPreviewCatalogBlock>
                                            <h4>{copy.recurringServices}</h4>
                                            {recurringCatalogByType.map((typeCatalogItem) => (
                                                <NewOfferPreviewCatalogType key={`recurring_${typeCatalogItem.typeKey}`}>
                                                    <h4>{typeCatalogItem.typeTitle}</h4>
                                                    <NewOfferPreviewCatalogTable>
                                                        <thead>
                                                            <tr>
                                                                <th><h6>{copy.serviceColumn}</h6></th>
                                                                <th><h6>{copy.catalogPriceColumn}</h6></th>
                                                                <th><h6>{copy.discountPercentColumn}</h6></th>
                                                                <th><h6>{copy.discountValueColumn}</h6></th>
                                                                <th><h6>{copy.finalValueColumn}</h6></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {typeCatalogItem.catalogRows.map((catalogRow) => (
                                                                <tr key={catalogRow.serviceKey}>
                                                                    <td><p>{catalogRow.serviceName}</p></td>
                                                                    <td><p>{eur(catalogRow.catalogPrice)}</p></td>
                                                                    <td><p>{`${catalogRow.discountPercent.toFixed(0)}%`}</p></td>
                                                                    <td><p>{eur(catalogRow.discountValue)}</p></td>
                                                                    <td><p>{eur(catalogRow.finalPrice)}</p></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </NewOfferPreviewCatalogTable>
                                                    <NewOfferPreviewCatalogSummary>
                                                        <h6>{copy.packageBenefit(eur(typeCatalogItem.totalDiscountValue), typeCatalogItem.totalDiscountPercent.toFixed(0))}</h6>
                                                    </NewOfferPreviewCatalogSummary>
                                                </NewOfferPreviewCatalogType>
                                            ))}
                                        </NewOfferPreviewCatalogBlock>

                                        <NewOfferPreviewCatalogBlock>
                                            <h4>{copy.oneOffSetupCost}</h4>
                                            {oneOffCatalogByType.map((typeCatalogItem) => (
                                                <NewOfferPreviewCatalogType key={`one_off_${typeCatalogItem.typeKey}`}>
                                                    <h4>{typeCatalogItem.typeTitle}</h4>
                                                    <NewOfferPreviewCatalogTable>
                                                        <thead>
                                                            <tr>
                                                                <th><h6>{copy.serviceColumn}</h6></th>
                                                                <th><h6>{copy.catalogPriceColumn}</h6></th>
                                                                <th><h6>{copy.discountPercentColumn}</h6></th>
                                                                <th><h6>{copy.discountValueColumn}</h6></th>
                                                                <th><h6>{copy.finalValueColumn}</h6></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {typeCatalogItem.catalogRows.map((catalogRow) => (
                                                                <tr key={catalogRow.serviceKey}>
                                                                    <td><p>{catalogRow.serviceName}</p></td>
                                                                    <td><p>{eur(catalogRow.catalogPrice)}</p></td>
                                                                    <td><p>{`${catalogRow.discountPercent.toFixed(0)}%`}</p></td>
                                                                    <td><p>{eur(catalogRow.discountValue)}</p></td>
                                                                    <td><p>{eur(catalogRow.finalPrice)}</p></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </NewOfferPreviewCatalogTable>
                                                    <NewOfferPreviewCatalogSummary>
                                                        <h6>{copy.packageBenefit(eur(typeCatalogItem.totalDiscountValue), typeCatalogItem.totalDiscountPercent.toFixed(0))}</h6>
                                                    </NewOfferPreviewCatalogSummary>
                                                </NewOfferPreviewCatalogType>
                                            ))}
                                        </NewOfferPreviewCatalogBlock>
                                    </>
                                ) : (
                                    <>
                                        {serviceCatalogByType.map((typeCatalogItem) => (
                                            <NewOfferPreviewCatalogType key={typeCatalogItem.typeKey}>
                                                <h4>{typeCatalogItem.typeTitle}</h4>
                                                <NewOfferPreviewCatalogTable>
                                                    <thead>
                                                        <tr>
                                                            <th><h6>{copy.serviceColumn}</h6></th>
                                                            <th><h6>{copy.catalogPriceColumn}</h6></th>
                                                            <th><h6>{copy.discountPercentColumn}</h6></th>
                                                            <th><h6>{copy.discountValueColumn}</h6></th>
                                                            <th><h6>{copy.finalValueColumn}</h6></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {typeCatalogItem.catalogRows.map((catalogRow) => (
                                                            <tr key={catalogRow.serviceKey}>
                                                                <td><p>{catalogRow.serviceName}</p></td>
                                                                <td><p>{eur(catalogRow.catalogPrice)}</p></td>
                                                                <td><p>{`${catalogRow.discountPercent.toFixed(0)}%`}</p></td>
                                                                <td><p>{eur(catalogRow.discountValue)}</p></td>
                                                                <td><p>{eur(catalogRow.finalPrice)}</p></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </NewOfferPreviewCatalogTable>
                                                <NewOfferPreviewCatalogSummary>
                                                    <h6>{copy.packageBenefit(eur(typeCatalogItem.totalDiscountValue), typeCatalogItem.totalDiscountPercent.toFixed(0))}</h6>
                                                </NewOfferPreviewCatalogSummary>
                                            </NewOfferPreviewCatalogType>
                                        ))}
                                    </>
                                )}
                            </NewOfferPreviewCatalog>
                        </NewOfferPreviewSection>
                    ) : null}

                    {!usesStripeSubscriptions && showAnyPricing ? (
                        <NewOfferPreviewSection>
                            <h3>{copy.offerPricing}</h3>
                            <NewOfferPreviewPricing>
                                {showMixedPricing ? (
                                <NewOfferPreviewPricingGrid>
                                    <NewOfferPreviewPricingCard>
                                        <NewOfferPreviewPricingHead>
                                            <h4>{copy.recurringServices}</h4>
                                        </NewOfferPreviewPricingHead>
                                        <NewOfferPreviewPricingRow>
                                            <p>{copy.netAmount}</p>
                                            <h5>{eur(recurringNetValue)}</h5>
                                        </NewOfferPreviewPricingRow>
                                        <NewOfferPreviewPricingRow>
                                            <p>{copy.vatAmount}</p>
                                            <h5>{eur(recurringVatValue)}</h5>
                                        </NewOfferPreviewPricingRow>
                                        <NewOfferPreviewPricingRow>
                                            <p>{copy.paymentMethod}</p>
                                            <h5>{recurringPaymentMethodLabel}</h5>
                                        </NewOfferPreviewPricingRow>
                                        <NewOfferPreviewPricingTotal>
                                            <p>{copy.totalWithVat}</p>
                                            <h3>{`${eur(recurringTotalValue)} ${copy.recurringSuffix}`}</h3>
                                        </NewOfferPreviewPricingTotal>
                                    </NewOfferPreviewPricingCard>

                                    <NewOfferPreviewPricingCard>
                                        <NewOfferPreviewPricingHead>
                                            <h4>{copy.oneOffSetupCost}</h4>
                                        </NewOfferPreviewPricingHead>
                                        <NewOfferPreviewPricingRow>
                                            <p>{copy.netAmount}</p>
                                            <h5>{eur(oneOffNetValue)}</h5>
                                        </NewOfferPreviewPricingRow>
                                        <NewOfferPreviewPricingRow>
                                            <p>{copy.vatAmount}</p>
                                            <h5>{eur(oneOffVatValue)}</h5>
                                        </NewOfferPreviewPricingRow>
                                        <NewOfferPreviewPricingRow>
                                            <p>{copy.paymentMethod}</p>
                                            <h5>{oneOffPaymentMethodLabel}</h5>
                                        </NewOfferPreviewPricingRow>
                                        <NewOfferPreviewPricingTotal>
                                            <p>{copy.totalWithVat}</p>
                                            <h3>{eur(oneOffTotalValue)}</h3>
                                        </NewOfferPreviewPricingTotal>
                                    </NewOfferPreviewPricingCard>
                                </NewOfferPreviewPricingGrid>
                                ) : (
                                <NewOfferPreviewPricingCard>
                                    <NewOfferPreviewPricingHead>
                                        <h4>{singlePricingIsRecurring ? copy.recurringServices : copy.oneOffSetupCost}</h4>
                                    </NewOfferPreviewPricingHead>
                                    <NewOfferPreviewPricingRow>
                                        <p>{copy.netAmount}</p>
                                        <h5>{eur(singlePricingNetValue)}</h5>
                                    </NewOfferPreviewPricingRow>
                                    <NewOfferPreviewPricingRow>
                                        <p>{copy.vatAmount}</p>
                                        <h5>{eur(singlePricingVatValue)}</h5>
                                    </NewOfferPreviewPricingRow>
                                    <NewOfferPreviewPricingRow>
                                        <p>{copy.paymentMethod}</p>
                                        <h5>{singlePricingMethodLabel}</h5>
                                    </NewOfferPreviewPricingRow>
                                    <NewOfferPreviewPricingTotal>
                                        <p>{copy.totalWithVat}</p>
                                        <h3>{singlePricingIsRecurring ? `${eur(singlePricingTotalValue)} ${copy.recurringSuffix}` : eur(singlePricingTotalValue)}</h3>
                                    </NewOfferPreviewPricingTotal>
                                </NewOfferPreviewPricingCard>
                                )}
                                <NewOfferPreviewPricingNote>{copy.validityNote(validFor)}</NewOfferPreviewPricingNote>
                            </NewOfferPreviewPricing>
                        </NewOfferPreviewSection>
                    ) : null}
                    <NewOfferPreviewFooter>
                        <h4>Doitforme</h4>
                        <h5>{copy.slogan}</h5>
                        <p>{copy.supportLine}</p>
                        {onlineOfferUrl.length ? (
                            <p>
                                <a href={onlineOfferUrl} target="_blank" rel="noreferrer">{onlineOfferUrl}</a>
                            </p>
                        ) : null}
                        <p>doitforme.eu</p>
                        <p>support@doitforme.eu</p>
                        <p>(0030) 24211 81 739 • (0030) 24211 81 740 • (0030) 24211 81 741</p>
                        <p>Mikrasiaton 75, Volos, Greece</p>
                    </NewOfferPreviewFooter>
                </NewOfferPreviewBody>
            </NewOfferPreviewPaper>
            {!props.hideActions ? (
                <NewOfferPreviewActions>
                    <New_Offer_Price_Summary {...priceSummaryProps} />
                    {render_reviewer_suggested_pricing_note('reviewer-pricing-bottom')}
                    <Actions_>
                        <Grey_
                            text={t('Back')}
                            iconLeft="Back"
                            onClick={props.onBack}
                        />
                        {props.allowFakeOffer === true ? <Offer_Creation_Action
                            offer={{ ...props.offerDraft, selectedType: props.offerDraft.selectedTypes?.[0] }}
                            fakeOffer={props.fakeOffer}
                            saving={props.isSaving} text={t(props.actionText || 'Create and Send')}
                            onCreate={create_and_send}
                        ></Offer_Creation_Action> : <Main_ text={t(props.actionText || 'Create and Send')} onClick={() => create_and_send()}></Main_>}
                    </Actions_>
                </NewOfferPreviewActions>
            ) : null}
        </NewOfferPreviewPage>
    );
}

export { New_Offer_Preview_Page };
